import {useState, useEffect, Component, Key} from "react";

// ─── Theme ────────────────────────────────────────────────────────────────────
const BG = "#1a3a1a";
const BG2 = "#1f421f";
const BG3 = "#244d24";
const BORDER = "rgba(255,255,255,0.08)";
const G = "#c9a84c";
const G2 = "#e8c96a";
const WHITE = "#f0ede6";
const DIM = "rgba(240,237,230,0.45)";
const GACC = "#4a9e4a";
const RED = "#c0392b";

// ─── Constants ────────────────────────────────────────────────────────────────
const DAYS = [
    {id: "thu", label: "Thursday", short: "Thu", tag: "Round 1"},
    {id: "fri", label: "Friday", short: "Fri", tag: "Round 2"},
    {id: "sat", label: "Saturday", short: "Sat", tag: "Round 3"},
    {id: "sun", label: "Sunday", short: "Sun", tag: "Round 4"},
];
const SWAG_ITEMS = [
    "Hat",
    "Shirt",
    "Jacket",
    "Bag",
    "Umbrella",
    "Cooler",
    "Yeti",
    "Other",
];
const MEAL_SLOTS = [
    "Thursday Breakfast",
    "Thursday Dinner",
    "Friday Breakfast",
    "Friday Dinner",
    "Saturday Breakfast",
    "Saturday Dinner",
    "Sunday Breakfast",
    "Sunday Dinner",
];
const GROCERY_CATEGORIES = [
    {id: "produce", label: "🥦 Produce"},
    {id: "protein", label: "🥩 Protein & Meat"},
    {id: "dairy", label: "🥛 Dairy & Eggs"},
    {id: "bakery", label: "🍞 Bread & Bakery"},
    {id: "pantry", label: "🫙 Pantry & Dry Goods"},
    {id: "snacks", label: "🧀 Snacks & Appetizers"},
    {id: "beer", label: "🍺 Beer"},
    {id: "wine", label: "🍷 Wine & Spirits"},
    {id: "nonalc", label: "🧃 Non-Alcoholic Drinks"},
    {id: "supplies", label: "🧴 Supplies & Paper Goods"},
];
const CLEANING_TASK_TEMPLATES = [
    "Vacuum all rooms",
    "Mop kitchen & bathrooms",
    "Clean & restock bathrooms",
    "Change bed linens",
    "Empty trash & recycling",
    "Wipe down kitchen counters",
    "Load / run dishwasher",
    "Clean grill",
    "Tidy common areas",
    "Restock drinks & ice",
    "Sweep porch & outdoor areas",
    "Wipe down dining table",
];
const CLEANING_SLOTS = [
    {id: "wed-eve", label: "Wed Evening — Pre-arrival"},
    {id: "thu-morn", label: "Thu Morning"},
    {id: "thu-eve", label: "Thu Evening"},
    {id: "fri-morn", label: "Fri Morning"},
    {id: "fri-eve", label: "Fri Evening"},
    {id: "sat-morn", label: "Sat Morning"},
    {id: "sat-eve", label: "Sat Evening"},
    {id: "sun-morn", label: "Sun Morning"},
    {id: "sun-eve", label: "Sun — Departure Cleanup"},
];
const NAV = [
    {id: "dashboard", icon: "⛳", label: "Dashboard"},
    {id: "guests", icon: "👤", label: "Guests"},
    {id: "hosting", icon: "🤝", label: "Host Assignments"},
    {id: "badges", icon: "🎫", label: "Badges"},
    {id: "reservations", icon: "📌", label: "Reservations"},
    {id: "rooms", icon: "🛏", label: "Rooms"},
    {id: "meals", icon: "🍽", label: "Schedule"},
    {id: "swag", icon: "🎁", label: "Swag"},
    {id: "grocery", icon: "🛒", label: "Food & Grocery"},
    {id: "cleaning", icon: "🧹", label: "Cleaning"},
    {id: "comms", icon: "📋", label: "Templates"},
    {id: "settings", icon: "⚙️", label: "Settings"},
];

// ─── Defaults ─────────────────────────────────────────────────────────────────
const defaultSettings = {
    houseName: "Augusta House",
    houseAddress: "123 Washington Rd, Augusta, GA 30904",
    eventYear: "2026",
    companyName: "Acme Corp",
    apiKey: "",
    badgesByDay: {thu: 0, fri: 0, sat: 0, sun: 0},
    swagItems: [
        "Hat",
        "Shirt",
        "Jacket",
        "Bag",
        "Umbrella",
        "Cooler",
        "Yeti",
        "Other",
    ],
    rooms: [
        {id: "r1", name: "Master Suite", beds: "King", capacity: 2},
        {id: "r2", name: "Garden Room", beds: "Queen", capacity: 2},
        {id: "r3", name: "Augusta Room", beds: "Queen", capacity: 2},
        {id: "r4", name: "Amen Corner", beds: "2 Twins", capacity: 2},
    ],
};
const mkGuest = () => ({
    id: Date.now().toString(),
    name: "",
    company: "",
    email: "",
    phone: "",
    type: "client",
    status: "soft-invite",
    arrivalDay: "thu",
    departureDay: "sun",
    roomId: "",
    hostId: "",
    needsHousing: true,
    badgeHold: false,
    badgeHoldDays: [],
    badgeDays: [],
    swag: {},
    notes: "",
    dietaryRestrictions: "",
    mealDays: [],
});
const mkGroceries = () =>
    GROCERY_CATEGORIES.map((c) => ({categoryId: c.id, items: []}));
const mkCleaning = () =>
    CLEANING_SLOTS.map((s) => ({slotId: s.id, tasks: []}));

// ─── Storage ──────────────────────────────────────────────────────────────────
const SK = "masters-event-app-v1";
const load = async (k) => {
    try {
        const v = localStorage.getItem(k);
        return v ? JSON.parse(v) : null;
    } catch {
        return null;
    }
};
const save = async (k, v) => {
    try {
        localStorage.setItem(k, JSON.stringify(v));
    } catch {
    }
};
const dayIdx = (id) => DAYS.findIndex((d) => d.id === id);
const daysInRange = (a, b) =>
    DAYS.filter((_, i) => i >= dayIdx(a) && i <= dayIdx(b)).map((d) => d.id);

// ─── Seed Data ───────────────────────────────────────────────────────────────
const SEED_GUESTS = [
    {
        id: "host-1772140233470",
        name: "Cameron Curry",
        company: "",
        email: "",
        phone: "",
        type: "host",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "",
        needsHousing: true,
        badgeDays: [],
        swag: {},
        notes: "",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "host-1772140233471",
        name: "Dean Squires",
        company: "",
        email: "",
        phone: "",
        type: "host",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "",
        needsHousing: true,
        badgeDays: [],
        swag: {},
        notes: "",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "host-1772140233472",
        name: "Alex Parker",
        company: "",
        email: "",
        phone: "",
        type: "host",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "",
        needsHousing: true,
        badgeDays: [],
        swag: {},
        notes: "",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "host-1772140233473",
        name: "John DiStasi",
        company: "",
        email: "",
        phone: "",
        type: "host",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "",
        needsHousing: true,
        badgeDays: [],
        swag: {},
        notes: "",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "host-1772140233474",
        name: "Scott Barrett",
        company: "",
        email: "",
        phone: "",
        type: "host",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "",
        needsHousing: true,
        badgeDays: [],
        swag: {},
        notes: "",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "host-1772140233475",
        name: "TJ Harrison",
        company: "",
        email: "",
        phone: "",
        type: "host",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "",
        needsHousing: true,
        badgeDays: [],
        swag: {},
        notes: "",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "host-1772140233476",
        name: "Alva Jones",
        company: "",
        email: "",
        phone: "",
        type: "host",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "",
        needsHousing: true,
        badgeDays: [],
        swag: {},
        notes: "",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "host-1772140233477",
        name: "Mandie Hancock",
        company: "",
        email: "",
        phone: "",
        type: "host",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "",
        needsHousing: true,
        badgeDays: [],
        swag: {},
        notes: "",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "host-1772140233478",
        name: "Glenn Roman",
        company: "",
        email: "",
        phone: "",
        type: "host",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "",
        needsHousing: true,
        badgeDays: [],
        swag: {},
        notes: "",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "host-1772140233479",
        name: "Matt Browher",
        company: "",
        email: "",
        phone: "",
        type: "host",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "",
        needsHousing: true,
        badgeDays: [],
        swag: {},
        notes: "",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "host-1772140233480",
        name: "Vanessa Bush",
        company: "",
        email: "",
        phone: "",
        type: "host",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "",
        needsHousing: true,
        badgeDays: [],
        swag: {},
        notes: "",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234470",
        name: "Rich Markham",
        company: "Clario",
        email: "",
        phone: "",
        type: "client",
        status: "soft-invite",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233470",
        badgeDays: [],
        swag: {},
        notes: "Title: VP",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234471",
        name: "Darin Cline",
        company: "Safe-Guard",
        email: "",
        phone: "",
        type: "client",
        status: "soft-invite",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233471",
        badgeDays: [],
        swag: {},
        notes: "Title: COO",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234472",
        name: "Brian Shield",
        company: "Safe-Guard",
        email: "",
        phone: "",
        type: "client",
        status: "soft-invite",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233472",
        badgeDays: [],
        swag: {},
        notes: "Title: CTO",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234473",
        name: "Bart Edwards",
        company: "Safe-Guard",
        email: "",
        phone: "",
        type: "client",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233472",
        badgeDays: [],
        swag: {},
        notes: "Title: VP",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234474",
        name: "Luis Milani",
        company: "Safe-Guard",
        email: "",
        phone: "",
        type: "client",
        status: "soft-invite",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233471",
        badgeDays: [],
        swag: {},
        notes: "",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234475",
        name: "Jay Taylor",
        company: "Chick-fil-A",
        email: "",
        phone: "",
        type: "client",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233473",
        badgeDays: [],
        swag: {},
        notes: "Title: VP",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234476",
        name: "Jarrod Redden",
        company: "Chick-fil-A",
        email: "",
        phone: "",
        type: "client",
        status: "soft-invite",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233473",
        badgeDays: [],
        swag: {},
        notes: "Title: Sr. Dir",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234477",
        name: "Quenon Smith",
        company: "Chick-fil-A",
        email: "",
        phone: "",
        type: "client",
        status: "soft-invite",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233473",
        badgeDays: [],
        swag: {},
        notes: "Title: Sr. Dir",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234478",
        name: "Chris Taylor",
        company: "Chick-fil-A",
        email: "",
        phone: "",
        type: "client",
        status: "soft-invite",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233473",
        badgeDays: [],
        swag: {},
        notes: "Title: Sr. Dir",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234479",
        name: "Chris Baumann",
        company: "Chick-fil-A",
        email: "",
        phone: "",
        type: "client",
        status: "soft-invite",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233474",
        badgeDays: [],
        swag: {},
        notes: "",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234480",
        name: "Doubea Pierre",
        company: "Chick-fil-A",
        email: "",
        phone: "",
        type: "client",
        status: "soft-invite",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233474",
        badgeDays: [],
        swag: {},
        notes: "",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234481",
        name: "Trent Boyd",
        company: "Chick-fil-A",
        email: "",
        phone: "",
        type: "client",
        status: "soft-invite",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233475",
        badgeDays: [],
        swag: {},
        notes: "",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234482",
        name: "Matt Simoneau",
        company: "Chick-fil-A",
        email: "",
        phone: "",
        type: "client",
        status: "soft-invite",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233475",
        badgeDays: [],
        swag: {},
        notes: "",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234483",
        name: "Jaimi Panini",
        company: "Bright Tree (Resmed)",
        email: "",
        phone: "",
        type: "client",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233472",
        badgeDays: [],
        swag: {},
        notes: "Title: VP",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234484",
        name: "Addison Perrymond",
        company: "Bright Tree (Resmed)",
        email: "",
        phone: "",
        type: "client",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233472",
        badgeDays: [],
        swag: {},
        notes: "Title: CPO",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234485",
        name: "Justin Leong",
        company: "Resmed",
        email: "",
        phone: "",
        type: "client",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233472",
        badgeDays: [],
        swag: {},
        notes: "Title: CPO",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234486",
        name: "Tressa Mild",
        company: "TransUnion",
        email: "",
        phone: "",
        type: "client",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233476",
        badgeDays: [],
        swag: {},
        notes: "Title: SVP",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234487",
        name: "Aaron Morgon",
        company: "Sofi",
        email: "",
        phone: "",
        type: "client",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233477",
        badgeDays: [],
        swag: {},
        notes: "Title: Director",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234488",
        name: "Adam Colclasure",
        company: "Sofi",
        email: "",
        phone: "",
        type: "client",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233477",
        badgeDays: [],
        swag: {},
        notes: "Title: Senior Director",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234489",
        name: "Tom Braun",
        company: "TIAA",
        email: "",
        phone: "",
        type: "client",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233478",
        badgeDays: [],
        swag: {},
        notes: "Title: Director Wealth Management",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234490",
        name: "Tim Ramsden",
        company: "Duke Energy",
        email: "",
        phone: "",
        type: "client",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233478",
        badgeDays: [],
        swag: {},
        notes: "Title: General manager , Customer Technology",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234491",
        name: "Ravi Mohan",
        company: "Reyes Beverage Group",
        email: "",
        phone: "",
        type: "client",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233479",
        badgeDays: [],
        swag: {},
        notes: "Title: VP, eCommerce & Digital Strategy",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234492",
        name: "Anna Labuda",
        company: "Duke Energy",
        email: "",
        phone: "",
        type: "client",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233480",
        badgeDays: [],
        swag: {},
        notes: "Title: Director - Active Client",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234493",
        name: "Brett Dahlin",
        company: "Alcon",
        email: "",
        phone: "",
        type: "client",
        status: "soft-invite",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233472",
        badgeDays: [],
        swag: {},
        notes: "Title: Director",
        dietaryRestrictions: "",
        mealDays: [],
    },
    {
        id: "client-1772140234494",
        name: "Kerry Holley",
        company: "Duke Energy",
        email: "",
        phone: "",
        type: "client",
        status: "confirmed",
        arrivalDay: "thu",
        departureDay: "sun",
        roomId: "",
        hostId: "host-1772140233478",
        badgeDays: [],
        swag: {},
        notes: "Title: Director Customer Solutions",
        dietaryRestrictions: "",
        mealDays: [],
    },
];

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
    const [tab, setTab] = useState("dashboard");
    const [settings, setSettings] = useState(defaultSettings);
    const [guests, setGuests] = useState(SEED_GUESTS);
    const [groceries, setGroceries] = useState(mkGroceries());
    const [cleaning, setCleaning] = useState(mkCleaning());
    const [reservations, setReservations] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        (async () => {
            const s = await load(SK + ":settings"),
                g = await load(SK + ":guests"),
                gr = await load(SK + ":groceries"),
                cl = await load(SK + ":cleaning"),
                rv = await load(SK + ":reservations");
            if (s) setSettings(s);
            // Use localStorage only if it has data with 10+ guests (real saved state)
            // otherwise always use the seeded data from the Excel import
            if (g && Array.isArray(g) && g.length >= 10) {
                setGuests(g);
            }
            if (gr) setGroceries(gr);
            if (cl) setCleaning(cl);
            if (rv) setReservations(rv);
            setLoaded(true);
        })();
    }, []);
    useEffect(() => {
        if (loaded) save(SK + ":settings", settings);
    }, [settings, loaded]);
    useEffect(() => {
        if (loaded) save(SK + ":guests", guests);
    }, [guests, loaded]);
    useEffect(() => {
        if (loaded) save(SK + ":groceries", groceries);
    }, [groceries, loaded]);
    useEffect(() => {
        if (loaded) save(SK + ":cleaning", cleaning);
    }, [cleaning, loaded]);
    useEffect(() => {
        if (loaded) save(SK + ":reservations", reservations);
    }, [reservations, loaded]);

    const addGuest = () => setGuests((g) => [...g, mkGuest()]);
    const updateGuest = (id, p) =>
        setGuests((g) => g.map((x) => (x.id === id ? {...x, ...p} : x)));
    const removeGuest = (id) => setGuests((g) => g.filter((x) => x.id !== id));

    if (!loaded)
        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100vh",
                    background: BG,
                }}
            >
                <div
                    style={{
                        width: 40,
                        height: 40,
                        border: `3px solid ${BG3}`,
                        borderTop: `3px solid ${G}`,
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                    }}
                />
                <p
                    style={{
                        color: G,
                        fontFamily: "Georgia,serif",
                        marginTop: 16,
                        fontSize: 18,
                    }}
                >
                    Loading your event...
                </p>
                <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
        );

    return (
        <div
            style={{
                display: "flex",
                minHeight: "100vh",
                background: BG,
                fontFamily: "Georgia,'Times New Roman',serif",
                color: WHITE,
            }}
        >
            <style>{CSS}</style>
            <aside style={S.sidebar}>
                <div style={S.sideTop}>
                    <div style={{fontSize: 36, marginBottom: 8}}>⛳</div>
                    <div style={S.sideTitle}>
                        Masters
                        <br/>
                        Planner {settings.eventYear}
                    </div>
                    <div style={S.sideSub}>
                        {guests.filter((g) => g.status === "confirmed").length} confirmed ·{" "}
                        {guests.filter((g) => g.status === "soft-invite").length} soft
                        invite
                    </div>
                </div>
                <nav style={{flex: 1, paddingTop: 8}}>
                    {NAV.map((n) => (
                        <button
                            key={n.id}
                            className="nav-btn"
                            style={{...S.navBtn, ...(tab === n.id ? S.navActive : {})}}
                            onClick={() => setTab(n.id)}
                        >
              <span style={{fontSize: 16, width: 22, textAlign: "center"}}>
                {n.icon}
              </span>
                            <span>{n.label}</span>
                        </button>
                    ))}
                </nav>
                <div style={S.sideFooter}>Augusta National · {settings.eventYear}</div>
            </aside>

            <main style={S.main}>
                {tab === "dashboard" && (
                    <Dashboard
                        guests={guests}
                        settings={settings}
                        groceries={groceries}
                        cleaning={cleaning}
                        setTab={setTab}
                    />
                )}
                {tab === "guests" && (
                    <GuestRoster
                        guests={guests}
                        settings={settings}
                        addGuest={addGuest}
                        updateGuest={updateGuest}
                        removeGuest={removeGuest}
                    />
                )}
                {tab === "hosting" && (
                    <HostAssignments guests={guests} updateGuest={updateGuest}/>
                )}
                {tab === "badges" && (
                    <BadgeTracker
                        guests={guests}
                        settings={settings}
                        setSettings={setSettings}
                    />
                )}
                {tab === "reservations" && (
                    <Reservations
                        guests={guests}
                        reservations={reservations}
                        setReservations={setReservations}
                        settings={settings}
                    />
                )}
                {tab === "rooms" && (
                    <RoomPlanner
                        guests={guests}
                        settings={settings}
                        setSettings={setSettings}
                        updateGuest={updateGuest}
                    />
                )}
                {tab === "meals" && (
                    <MealPlanner guests={guests} updateGuest={updateGuest}/>
                )}
                {tab === "swag" && (
                    <SwagTracker
                        guests={guests}
                        updateGuest={updateGuest}
                        settings={settings}
                        setSettings={setSettings}
                    />
                )}
                {tab === "grocery" && (
                    <GroceryPlanner
                        groceries={groceries}
                        setGroceries={setGroceries}
                        guests={guests}
                    />
                )}
                {tab === "cleaning" && (
                    <CleaningSchedule
                        cleaning={cleaning}
                        setCleaning={setCleaning}
                        guests={guests}
                    />
                )}
                {tab === "comms" && (
                    <CommTemplates guests={guests} settings={settings}/>
                )}
                {tab === "settings" && (
                    <SettingsPage settings={settings} setSettings={setSettings}/>
                )}
            </main>
        </div>
    );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
function Dashboard({guests, settings, groceries, cleaning, setTab}) {
    const clients = guests.filter((g) => g.type === "client");
    const hosts = guests.filter((g) => g.type === "host");
    const totBadges = Object.values(settings.badgesByDay).reduce(
        (a: number, b) => a + Number(b),
        0
    );
    const usedBadge = guests.reduce((s, g) => s + (g.badgeDays?.length || 0), 0);
    const totBeds = settings.rooms.reduce((s, r) => s + r.capacity, 0);
    const roomsOcc = settings.rooms.filter((r) =>
        guests.some((g) => g.roomId === r.id)
    ).length;
    const swagCount = guests.reduce(
        (s, g) => s + Object.values(g.swag || {}).filter(Boolean).length,
        0
    );
    const totGr = (groceries || []).reduce((s, c) => s + c.items.length, 0);
    const doneGr = (groceries || []).reduce(
        (s, c) => s + c.items.filter((i) => i.checked).length,
        0
    );
    const totCl = (cleaning || []).reduce((s, sl) => s + sl.tasks.length, 0);
    const doneCl = (cleaning || []).reduce(
        (s, sl) => s + sl.tasks.filter((t) => t.done).length,
        0
    );

    return (
        <div>
            <div style={S.pageHeader}>
                <h1 style={S.pageTitle}>Masters Week {settings.eventYear}</h1>
                <div style={{fontSize: 14, color: DIM, marginTop: 4}}>
                    {settings.houseName} · {settings.companyName}
                </div>
            </div>

            <div style={S.statGrid}>
                <StatCard
                    icon="👥"
                    label="Confirmed"
                    value={guests.filter((g) => g.status === "confirmed").length}
                    sub={`${
                        guests.filter((g) => g.status === "soft-invite").length
                    } soft invite · ${
                        guests.filter((g) => g.status === "declined").length
                    } declined`}
                    onClick={() => setTab("guests")}
                />
                <StatCard
                    icon="🛏"
                    label="Rooms"
                    value={`${roomsOcc}/${settings.rooms.length}`}
                    sub={`${totBeds} total beds`}
                    onClick={() => setTab("rooms")}
                />
                <StatCard
                    icon="🎁"
                    label="Swag Items"
                    value={swagCount}
                    sub={`${
                        guests.filter((g) => Object.keys(g.swag || {}).length > 0).length
                    } recipients`}
                    onClick={() => setTab("swag")}
                />
                <StatCard
                    icon="🧹"
                    label="Clean Tasks"
                    value={totCl}
                    sub={`${doneCl} done`}
                    onClick={() => setTab("cleaning")}
                />
            </div>

            {/* Badge allocations */}
            <DashSection title="🎫 Badge Allocations">
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4,1fr)",
                        gap: 12,
                    }}
                >
                    {DAYS.map((d) => {
                        const alloc = Number(settings.badgesByDay[d.id] || 0);
                        const assigned = guests.filter((g) =>
                            g.badgeDays?.includes(d.id)
                        ).length;
                        const pct = alloc > 0 ? Math.min((assigned / alloc) * 100, 100) : 0;
                        return (
                            <div key={d.id} style={S.badgeCard}>
                                <div style={{fontWeight: 700, fontSize: 14, color: WHITE}}>
                                    {d.label}
                                </div>
                                <div style={{fontSize: 11, color: DIM}}>{d.tag}</div>
                                <div
                                    style={{
                                        fontSize: 26,
                                        fontWeight: 700,
                                        color: G2,
                                        fontFamily: "Georgia,serif",
                                        marginTop: 8,
                                    }}
                                >
                                    {assigned}/{alloc}
                                </div>
                                <div style={{...S.progBg, marginTop: 8}}>
                                    <div
                                        style={{
                                            ...S.progBar,
                                            width: `${pct}%`,
                                            background: pct >= 100 ? RED : G,
                                        }}
                                    />
                                </div>
                                <div style={{fontSize: 12, color: DIM, marginTop: 6}}>
                                    {alloc - assigned} remaining
                                </div>
                            </div>
                        );
                    })}
                </div>
            </DashSection>

            {/* Nightly occupancy */}
            <DashSection title="🏠 Nightly Occupancy">
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4,1fr)",
                        gap: 12,
                    }}
                >
                    {DAYS.map((d) => {
                        const occ = guests.filter(
                            (g) =>
                                g.status === "confirmed" &&
                                daysInRange(g.arrivalDay, g.departureDay).includes(d.id)
                        );
                        return (
                            <div key={d.id} style={S.badgeCard}>
                                <div style={{fontWeight: 700, fontSize: 14, color: WHITE}}>
                                    {d.label}
                                </div>
                                <div style={{fontSize: 11, color: DIM, marginBottom: 8}}>
                                    night
                                </div>
                                {occ.length === 0 ? (
                                    <div style={{color: DIM, fontSize: 13}}>Vacant</div>
                                ) : (
                                    occ.map((g) => (
                                        <div
                                            key={g.id}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 6,
                                                marginTop: 6,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: 7,
                                                    height: 7,
                                                    borderRadius: "50%",
                                                    background: g.type === "host" ? GACC : G,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <span style={{fontSize: 13}}>
                        {g.name || "Unnamed"}
                      </span>
                                        </div>
                                    ))
                                )}
                            </div>
                        );
                    })}
                </div>
            </DashSection>

            {/* Arrivals & Departures */}
            <DashSection title="✈️ Arrivals & Departures">
                {DAYS.map((d) => {
                    const arr = guests.filter(
                        (g) => g.arrivalDay === d.id && g.name && g.status === "confirmed"
                    );
                    const dep = guests.filter(
                        (g) => g.departureDay === d.id && g.name && g.status === "confirmed"
                    );
                    if (!arr.length && !dep.length) return null;
                    return (
                        <div
                            key={d.id}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 14,
                                padding: "8px 0",
                                borderBottom: `1px solid ${BORDER}`,
                            }}
                        >
                            <div
                                style={{
                                    minWidth: 90,
                                    fontWeight: 600,
                                    fontSize: 14,
                                    color: G2,
                                }}
                            >
                                {d.label}
                            </div>
                            <div style={{display: "flex", flexWrap: "wrap", gap: 6}}>
                                {arr.map((g) => (
                                    <span
                                        key={g.id}
                                        style={{
                                            ...S.chip,
                                            background: "rgba(74,158,74,0.25)",
                                            color: "#7ecf7e",
                                            border: "1px solid rgba(74,158,74,0.3)",
                                        }}
                                    >
                    ↓ {g.name}
                  </span>
                                ))}
                                {dep.map((g) => (
                                    <span
                                        key={g.id}
                                        style={{
                                            ...S.chip,
                                            background: "rgba(192,57,43,0.2)",
                                            color: "#e88070",
                                            border: "1px solid rgba(192,57,43,0.3)",
                                        }}
                                    >
                    ↑ {g.name}
                  </span>
                                ))}
                            </div>
                        </div>
                    );
                })}
                {guests.length === 0 && <EmptyState>No guests added yet.</EmptyState>}
            </DashSection>
        </div>
    );
}

function StatCard({icon, label, value, sub, onClick}) {
    return (
        <div style={S.statCard} className="stat-card" onClick={onClick}>
            <div style={{fontSize: 26, marginBottom: 6}}>{icon}</div>
            <div
                style={{
                    fontSize: 32,
                    fontWeight: 700,
                    color: G2,
                    fontFamily: "Georgia,serif",
                }}
            >
                {value}
            </div>
            <div
                style={{fontSize: 13, fontWeight: 700, color: WHITE, marginTop: 4}}
            >
                {label}
            </div>
            <div style={{fontSize: 11, color: DIM, marginTop: 3}}>{sub}</div>
        </div>
    );
}

function DashSection({title, children}) {
    return (
        <div style={{marginBottom: 24}}>
            <div
                style={{fontWeight: 700, fontSize: 16, color: G2, marginBottom: 12}}
            >
                {title}
            </div>
            <div style={S.card}>{children}</div>
        </div>
    );
}

// ─── Guest Roster ─────────────────────────────────────────────────────────────
function GuestRoster({guests, settings, addGuest, updateGuest, removeGuest}) {
    const [open, setOpen] = useState(null);
    const [filter, setFilter] = useState("all");
    const [typeFilter, setTypeFilter] = useState("all");

    const statusCounts = {
        all: guests.length,
        "soft-invite": guests.filter((g) => g.status === "soft-invite").length,
        confirmed: guests.filter((g) => g.status === "confirmed").length,
        declined: guests.filter((g) => g.status === "declined").length,
    };

    const filtered = guests.filter((g) => {
        const statusMatch = filter === "all" || g.status === filter;
        const typeMatch = typeFilter === "all" || g.type === typeFilter;
        return statusMatch && typeMatch;
    });

    return (
        <div>
            <PH
                title="Guests"
                action={
                    <Btn
                        onClick={() => {
                            addGuest();
                            setOpen("last");
                        }}
                    >
                        + Add Guest
                    </Btn>
                }
            />

            {/* Status pipeline summary */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3,1fr)",
                    gap: 10,
                    marginBottom: 16,
                }}
            >
                {[
                    ["soft-invite", "💌", "Soft Invite", "rgba(201,168,76,0.15)", G],
                    ["confirmed", "✅", "Confirmed", "rgba(74,158,74,0.15)", "#7ecf7e"],
                    ["declined", "❌", "Declined", "rgba(192,57,43,0.15)", "#e88070"],
                ].map(([s, icon, label, bg, color]) => (
                    <div
                        key={s}
                        style={{
                            background: bg,
                            border: `1px solid ${
                                filter === s ? "rgba(255,255,255,0.2)" : BORDER
                            }`,
                            borderRadius: 10,
                            padding: "12px 16px",
                            cursor: "pointer",
                            transition: "all .15s",
                        }}
                        onClick={() => setFilter((f) => (f === s ? "all" : s))}
                    >
                        <div style={{fontSize: 20}}>{icon}</div>
                        <div
                            style={{
                                fontSize: 24,
                                fontWeight: 700,
                                color,
                                fontFamily: "Georgia,serif",
                                marginTop: 4,
                            }}
                        >
                            {statusCounts[s]}
                        </div>
                        <div style={{fontSize: 12, color: DIM, marginTop: 2}}>
                            {label}
                        </div>
                    </div>
                ))}
            </div>

            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 12,
                    flexWrap: "wrap",
                    gap: 8,
                }}
            >
                <FilterRow
                    options={[
                        ["all", "All"],
                        ["soft-invite", "💌 Soft Invite"],
                        ["confirmed", "✅ Confirmed"],
                        ["declined", "❌ Declined"],
                    ]}
                    value={filter}
                    onChange={setFilter}
                />
                <FilterRow
                    options={[
                        ["all", "All Types"],
                        ["client", "Clients"],
                        ["host", "Hosts"],
                    ]}
                    value={typeFilter}
                    onChange={setTypeFilter}
                />
            </div>

            {filtered.length === 0 && (
                <EmptyCard>No guests match this filter.</EmptyCard>
            )}
            {filtered.map((g, i) => (
                <GuestCard
                    key={g.id}
                    guest={g}
                    settings={settings}
                    guests={guests}
                    isOpen={open === g.id || (open === "last" && i === guests.length - 1)}
                    toggle={() => setOpen((o) => (o === g.id ? null : g.id))}
                    update={(p) => updateGuest(g.id, p)}
                    remove={() => {
                        removeGuest(g.id);
                        setOpen(null);
                    }}
                />
            ))}
        </div>
    );
}

function GuestCard({
                       guest,
                       settings,
                       guests,
                       isOpen,
                       toggle,
                       update,
                       remove,
                   }) {
    const assignedHost = guests?.find(
        (g) => g.id === guest.hostId && g.type === "host"
    );
    // @ts-ignore
    return (
        <div style={S.guestCard}>
            <div style={S.guestHead} className="clickable" onClick={toggle}>
                <div style={{display: "flex", alignItems: "center", gap: 12}}>
                    <div
                        style={{
                            ...S.avatar,
                            background: guest.type === "host" ? GACC : "#2a6496",
                        }}
                    >
                        {(guest.name || "?")[0].toUpperCase()}
                    </div>
                    <div>
                        <div style={{fontWeight: 700, fontSize: 16, color: WHITE}}>
                            {guest.name || (
                                <span style={{opacity: 0.4}}>Unnamed Guest</span>
                            )}
                        </div>
                        <div
                            style={{
                                fontSize: 13,
                                color: DIM,
                                marginTop: 2,
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                flexWrap: "wrap",
                            }}
                        >
                            {guest.company && <>{guest.company} · </>}
                            <span
                                style={{
                                    ...S.chip,
                                    padding: "1px 7px",
                                    fontSize: 11,
                                    background:
                                        guest.type === "host"
                                            ? "rgba(74,158,74,0.25)"
                                            : "rgba(42,100,150,0.3)",
                                    color: guest.type === "host" ? "#7ecf7e" : "#89c4f4",
                                }}
                            >
                {guest.type}
              </span>
                            <span
                                style={{
                                    ...S.chip,
                                    padding: "1px 7px",
                                    fontSize: 11,
                                    ...(guest.status === "confirmed"
                                        ? {background: "rgba(74,158,74,0.25)", color: "#7ecf7e"}
                                        : guest.status === "declined"
                                            ? {background: "rgba(192,57,43,0.2)", color: "#e88070"}
                                            : {background: "rgba(201,168,76,0.2)", color: G2}),
                                }}
                            >
                {guest.status === "confirmed"
                    ? "✅ Confirmed"
                    : guest.status === "declined"
                        ? "❌ Declined"
                        : "💌 Soft Invite"}
              </span>
                            {guest.type === "client" && assignedHost && (
                                <span
                                    style={{
                                        ...S.chip,
                                        padding: "1px 7px",
                                        fontSize: 11,
                                        background: "rgba(74,158,74,0.15)",
                                        color: "#7ecf7e",
                                    }}
                                >
                  🤝 {assignedHost.name}
                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div style={{display: "flex", alignItems: "center", gap: 8}}>
          <span style={S.chip}>
            {DAYS.find((d) => d.id === guest.arrivalDay)?.short} →{" "}
              {DAYS.find((d) => d.id === guest.departureDay)?.short}
          </span>
                    {guest.needsHousing === false && (
                        <span
                            style={{
                                ...S.chip,
                                background: "rgba(201,168,76,0.15)",
                                color: G2,
                                fontSize: 11,
                            }}
                        >
              🏨 Own stay
            </span>
                    )}
                    {guest.badgeHold && (
                        <span
                            style={{
                                ...S.chip,
                                background: "rgba(201,168,76,0.2)",
                                color: G2,
                                fontSize: 11,
                            }}
                        >
              📌{" "}
                            {guest.badgeHoldDays?.length
                                ? "Available: " +
                                guest.badgeHoldDays
                                    .map((id) => DAYS.find((d) => d.id === id)?.short)
                                    .join(", ")
                                : "Badge held (flexible)"}
            </span>
                    )}
                    <span style={{color: G, fontSize: 14}}>{isOpen ? "▲" : "▼"}</span>
                </div>
            </div>
            {isOpen && (
                <div
                    style={{padding: "0 20px 20px", borderTop: `1px solid ${BORDER}`}}
                >
                    <div style={S.formGrid}>
                        <Field label="Full Name">
                            <Inp
                                value={guest.name}
                                onChange={(e) => update({name: e.target.value})}
                                placeholder="Jane Smith"
                            />
                        </Field>
                        <Field label="Company">
                            <Inp
                                value={guest.company}
                                onChange={(e) => update({company: e.target.value})}
                                placeholder="Acme Inc"
                            />
                        </Field>
                        <Field label="Email">
                            <Inp
                                value={guest.email}
                                onChange={(e) => update({email: e.target.value})}
                                placeholder="jane@company.com"
                            />
                        </Field>
                        <Field label="Phone">
                            <Inp
                                value={guest.phone}
                                onChange={(e) => update({phone: e.target.value})}
                                placeholder="(555) 000-0000"
                            />
                        </Field>
                        <Field label="Guest Type">
                            <Sel
                                value={guest.type}
                                onChange={(e) => update({type: e.target.value})}
                            >
                                <option value="client">Client Guest</option>
                                <option value="host">Company Host</option>
                            </Sel>
                        </Field>
                        <Field label="Invite Status">
                            <Sel
                                value={guest.status || "soft-invite"}
                                onChange={(e) => update({status: e.target.value})}
                            >
                                <option value="soft-invite">💌 Soft Invite</option>
                                <option value="confirmed">✅ Confirmed</option>
                                <option value="declined">❌ Declined</option>
                            </Sel>
                        </Field>
                        {guest.type === "client" && (
                            <Field label="Assigned Host">
                                <Sel
                                    value={guest.hostId || ""}
                                    onChange={(e) => update({hostId: e.target.value})}
                                >
                                    <option value="">— Unassigned —</option>
                                    {guests
                                        ?.filter((g) => g.type === "host" && g.name)
                                        .map((h) => (
                                            <option key={h.id} value={h.id}>
                                                {h.name}
                                            </option>
                                        ))}
                                </Sel>
                            </Field>
                        )}
                        <Field label="Needs Housing">
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    marginTop: 4,
                                }}
                            >
                                <div
                                    style={{
                                        position: "relative",
                                        width: 44,
                                        height: 24,
                                        cursor: "pointer",
                                    }}
                                    onClick={() => update({needsHousing: !guest.needsHousing})}
                                >
                                    <div
                                        style={{
                                            position: "absolute",
                                            inset: 0,
                                            borderRadius: 12,
                                            background:
                                                guest.needsHousing !== false
                                                    ? "rgba(74,158,74,0.5)"
                                                    : "rgba(255,255,255,0.1)",
                                            transition: "background .2s",
                                        }}
                                    />
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: 3,
                                            left: guest.needsHousing !== false ? 22 : 3,
                                            width: 18,
                                            height: 18,
                                            borderRadius: "50%",
                                            background:
                                                guest.needsHousing !== false ? "#7ecf7e" : DIM,
                                            transition: "left .2s",
                                        }}
                                    />
                                </div>
                                <span
                                    style={{
                                        fontSize: 13,
                                        color: guest.needsHousing !== false ? "#7ecf7e" : DIM,
                                    }}
                                >
                  {guest.needsHousing !== false
                      ? "Yes — needs a room"
                      : "No — has own accommodations"}
                </span>
                            </div>
                        </Field>
                        {guest.needsHousing !== false && (
                            <Field label="Room">
                                <Sel
                                    value={guest.roomId}
                                    onChange={(e) => update({roomId: e.target.value})}
                                >
                                    <option value="">— Unassigned —</option>
                                    {settings.rooms.map((r) => (
                                        <option key={r.id} value={r.id}>
                                            {r.name} ({r.beds})
                                        </option>
                                    ))}
                                </Sel>
                            </Field>
                        )}
                        <Field label="Arrival">
                            <Sel
                                value={guest.arrivalDay}
                                onChange={(e) => update({arrivalDay: e.target.value})}
                            >
                                {DAYS.map((d) => (
                                    <option key={d.id} value={d.id}>
                                        {d.label}
                                    </option>
                                ))}
                            </Sel>
                        </Field>
                        <Field label="Departure">
                            <Sel
                                value={guest.departureDay}
                                onChange={(e) => update({departureDay: e.target.value})}
                            >
                                {DAYS.map((d) => (
                                    <option key={d.id} value={d.id}>
                                        {d.label}
                                    </option>
                                ))}
                            </Sel>
                        </Field>
                    </div>
                    <div style={{marginBottom: 12}}>
                        <Field label="Dietary Restrictions">
                            <Inp
                                value={guest.dietaryRestrictions}
                                onChange={(e) =>
                                    update({dietaryRestrictions: e.target.value})
                                }
                                placeholder="None, vegetarian, gluten-free…"
                            />
                        </Field>
                    </div>
                    <Field label="Badge Days">
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 10,
                                marginTop: 4,
                            }}
                        >
                            {DAYS.map((d) => (
                                <label
                                    key={d.id}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 5,
                                        fontSize: 14,
                                        cursor: "pointer",
                                        color: WHITE,
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={guest.badgeDays?.includes(d.id)}
                                        onChange={(e) =>
                                            update({
                                                badgeDays: e.target.checked
                                                    ? [...(guest.badgeDays || []), d.id]
                                                    : (guest.badgeDays || []).filter((x) => x !== d.id),
                                            })
                                        }
                                    />
                                    {d.short}
                                </label>
                            ))}
                        </div>
                    </Field>
                    <Field label="Reserve a Badge Spot">
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                marginTop: 4,
                            }}
                        >
                            <div
                                style={{
                                    position: "relative",
                                    width: 44,
                                    height: 24,
                                    cursor: "pointer",
                                }}
                                onClick={() => update({badgeHold: !guest.badgeHold})}
                            >
                                <div
                                    style={{
                                        position: "absolute",
                                        inset: 0,
                                        borderRadius: 12,
                                        background: guest.badgeHold
                                            ? "rgba(201,168,76,0.5)"
                                            : "rgba(255,255,255,0.1)",
                                        transition: "background .2s",
                                    }}
                                />
                                <div
                                    style={{
                                        position: "absolute",
                                        top: 3,
                                        left: guest.badgeHold ? 22 : 3,
                                        width: 18,
                                        height: 18,
                                        borderRadius: "50%",
                                        background: guest.badgeHold ? G2 : DIM,
                                        transition: "left .2s",
                                    }}
                                />
                            </div>
                            <span style={{fontSize: 13, color: guest.badgeHold ? G2 : DIM}}>
                {guest.badgeHold
                    ? "Yes — holding a badge spot"
                    : "No — not holding a spot yet"}
              </span>
                        </div>
                        {guest.badgeHold && (
                            <div
                                style={{
                                    marginTop: 10,
                                    padding: "10px 14px",
                                    background: "rgba(201,168,76,0.08)",
                                    borderRadius: 8,
                                    border: `1px solid rgba(201,168,76,0.2)`,
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 11,
                                        color: G,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.08em",
                                        marginBottom: 8,
                                    }}
                                >
                                    Available days{" "}
                                    <span
                                        style={{
                                            color: DIM,
                                            textTransform: "none",
                                            letterSpacing: 0,
                                        }}
                                    >
                    (check all that apply, leave blank if fully flexible)
                  </span>
                                </div>
                                <div style={{display: "flex", gap: 16, flexWrap: "wrap"}}>
                                    {DAYS.map((d) => (
                                        <label
                                            key={d.id}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 6,
                                                fontSize: 13,
                                                cursor: "pointer",
                                                color: guest.badgeHoldDays?.includes(d.id) ? G2 : WHITE,
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={guest.badgeHoldDays?.includes(d.id) || false}
                                                onChange={(e) =>
                                                    update({
                                                        badgeHoldDays: e.target.checked
                                                            ? [...(guest.badgeHoldDays || []), d.id]
                                                            : (guest.badgeHoldDays || []).filter(
                                                                (x) => x !== d.id
                                                            ),
                                                    })
                                                }
                                            />
                                            {d.label}
                                        </label>
                                    ))}
                                </div>
                                <div style={{fontSize: 11, color: DIM, marginTop: 8}}>
                                    {!guest.badgeHoldDays?.length
                                        ? "⚡ Fully flexible — AI will suggest the best day"
                                        : "📅 Available: " +
                                        guest.badgeHoldDays
                                            .map((id) => DAYS.find((d) => d.id === id)?.label)
                                            .join(", ")}
                                </div>
                            </div>
                        )}
                    </Field>
                    <div style={{marginTop: 12}}>
                        <Field label="Notes">
              <textarea
                  style={{...S.input, height: 56, resize: "vertical"}}
                  value={guest.notes}
                  onChange={(e) => update({notes: e.target.value})}
                  placeholder="VIP notes, preferences, host assignment…"
              />
                        </Field>
                    </div>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            marginTop: 14,
                        }}
                    >
                        <button style={S.btnDanger} onClick={remove}>
                            Remove Guest
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Reservations ─────────────────────────────────────────────────────────────
function Reservations({guests, reservations, setReservations, settings}) {
    const [newCo, setNewCo] = useState("");
    const [newDay, setNewDay] = useState("thu");
    const [newQty, setNewQty] = useState(1);

    const addReservation = () => {
        const co = newCo.trim();
        if (!co) return;
        setReservations((r) => [
            ...r,
            {
                id: Date.now().toString(),
                company: co,
                day: newDay,
                qty: Number(newQty),
                availableDays: [],
            },
        ]);
        setNewCo("");
        setNewQty(1);
    };
    const removeReservation = (id: any) =>
        setReservations((r) => r.filter((x) => x.id !== id));
    const updateReservation = (id: any, patch: { day?: any; qty?: number; availableDays?: any; }) =>
        setReservations((r: any[]) =>
            r.map((x) => (x.id === id ? {...x, ...patch} : x))
        );

    // For each reservation, find matching guests by company name.
    // Clients match by their company field.
    // Hosts match if ANY of their assigned clients belong to this company
    // (host is linked to client via client.hostId === host.id).
    // TBD reservations match regardless of badge day.
    const getMatches = (res) => {
        const co = res.company.toLowerCase().trim();
        const dayOk = (g) => res.day === "tbd" || g.badgeDays?.includes(res.day);
        // Find host IDs that have at least one client from this company
        const hostIdsForCompany = new Set(
            guests
                .filter(
                    (g) =>
                        g.type === "client" &&
                        g.company?.toLowerCase().trim() === co &&
                        g.hostId
                )
                .map((g) => g.hostId)
        );
        return guests.filter((g) => {
            if (g.type === "host") return hostIdsForCompany.has(g.id) && dayOk(g);
            return g.company?.toLowerCase().trim() === co && dayOk(g);
        });
    };

    const getConfirmed = (res) =>
        getMatches(res).filter((g) => g.status === "confirmed");
    const getInterested = (res) =>
        getMatches(res).filter((g) => g.status === "soft-invite");

    // Group reservations by company for a cleaner view
    let companies: any[];
    // @ts-ignore
    companies = [...new Set(reservations.map((r) => r.company))];

    // Known companies from guest list for autocomplete suggestions
    let knownCompanies: any[];
    // @ts-ignore
    knownCompanies = [...new Set(guests
        .filter((g) => g.type === "client" && g.company)
        .map((g) => g.company)
    ),].sort();

    // Personal holds = guests with badgeHold:true (day flexible, counts against total pool)
    const personalHolds = guests.filter((g) => g.badgeHold);

    // Per-day capacity summary
    const capacitySummary = DAYS.map((d) => {
        const alloc = Number(settings.badgesByDay?.[d.id] || 0);
        const confirmed = guests.filter(
            (g) => g.status === "confirmed" && g.badgeDays?.includes(d.id)
        ).length;
        const interested = guests.filter(
            (g) => g.status === "soft-invite" && g.badgeDays?.includes(d.id)
        ).length;
        const compRes = reservations
            .filter((r) => r.day === d.id)
            .reduce((s, r) => s + r.qty, 0);
        return {day: d, confirmed, interested, compRes};
    });
    const tbdRes = reservations
        .filter((r) => r.day === "tbd")
        .reduce((s, r) => s + r.qty, 0);
    const holdCount = personalHolds.length;

    const [aiSuggestion, setAiSuggestion] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiError, setAiError] = useState(null);

    const getSuggestion = async () => {
        setAiLoading(true);
        setAiSuggestion(null);
        setAiError(null);
        try {
            // Build a rich data snapshot to send to Claude
            const badgeAlloc = DAYS.map((d) => ({
                day: d.label,
                tag: d.tag,
                allocated: Number(settings.badgesByDay?.[d.id] || 0),
                confirmed: guests
                    .filter(
                        (g) => g.status === "confirmed" && g.badgeDays?.includes(d.id)
                    )
                    .map((g) => g.name || "Unknown"),
            }));

            const companyReservations = reservations.map((r) => ({
                company: r.company,
                day:
                    r.day === "tbd" ? "Day TBD" : DAYS.find((d) => d.id === r.day)?.label,
                availableDays:
                    r.day === "tbd" && r.availableDays?.length
                        ? r.availableDays
                            .map((id) => DAYS.find((d) => d.id === id)?.label)
                            .filter(Boolean)
                        : null,
                spotsReserved: r.qty,
                confirmedGuests: getMatches(r)
                    .filter((g) => g.status === "confirmed")
                    .map((g) => g.name),
                interestedGuests: getMatches(r)
                    .filter((g) => g.status === "soft-invite")
                    .map((g) => g.name),
            }));

            const personalHoldsList = personalHolds.map((g) => ({
                name: g.name || "Unknown",
                company: g.company || "",
                status: g.status,
                availableDays: g.badgeHoldDays?.length
                    ? g.badgeHoldDays
                        .map((id) => DAYS.find((d) => d.id === id)?.label)
                        .filter(Boolean)
                    : null,
            }));

            const badgeLines = badgeAlloc
                .map(
                    (d) =>
                        "- " +
                        d.day +
                        " (" +
                        d.tag +
                        "): " +
                        d.allocated +
                        " total badges, " +
                        d.confirmed.length +
                        " confirmed (" +
                        (d.confirmed.join(", ") || "none") +
                        "), " +
                        (d.allocated - d.confirmed.length) +
                        " remaining"
                )
                .join("\n");

            const resLines =
                companyReservations.length > 0
                    ? companyReservations
                        .map(
                            (r) =>
                                "- " +
                                r.company +
                                ": " +
                                r.spotsReserved +
                                " spots on " +
                                r.day +
                                (r.availableDays
                                    ? " (available days: " + r.availableDays.join(", ") + ")"
                                    : r.day === "Day TBD"
                                        ? " (fully flexible on day)"
                                        : "") +
                                ". Confirmed: " +
                                (r.confirmedGuests.join(", ") || "none") +
                                ". Interested: " +
                                (r.interestedGuests.join(", ") || "none")
                        )
                        .join("\n")
                    : "None";

            const holdLines =
                personalHoldsList.length > 0
                    ? personalHoldsList
                        .map(
                            (p) =>
                                "- " +
                                p.name +
                                (p.company ? " (" + p.company + ")" : "") +
                                ": status=" +
                                p.status +
                                ", available days=" +
                                (p.availableDays
                                    ? p.availableDays.join(", ")
                                    : "fully flexible — any day works")
                        )
                        .join("\n")
                    : "None";

            const prompt = [
                "You are helping plan badge allocations for the Masters golf tournament hospitality event.",
                "",
                "Here is the current badge situation:",
                "",
                "BADGE ALLOCATIONS BY DAY:",
                badgeLines,
                "",
                "COMPANY RESERVATIONS (spots being held):",
                resLines,
                "",
                "PERSONAL BADGE HOLDS (flexible on day, day TBD):",
                holdLines,
                "",
                "Please analyze this data and:",
                "1. Identify which days have capacity remaining after confirmed guests and reservations",
                "2. Suggest the best day(s) to assign each personal hold and TBD reservation, explaining your reasoning (try to spread demand evenly, respect any day preferences, avoid going over capacity)",
                "3. Flag any days that are at risk of being over-allocated",
                "4. Give an overall summary of badge health",
                "",
                "Be specific with names and numbers. Format your response clearly with sections.",
            ].join("\n");

            const res = await fetch("https://api.anthropic.com/v1/messages", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-api-key": settings.apiKey || "",
                    "anthropic-version": "2023-06-01",
                    "anthropic-dangerous-direct-browser-access": "true",
                },
                body: JSON.stringify({
                    model: "claude-sonnet-4-20250514",
                    max_tokens: 1000,
                    messages: [{role: "user", content: prompt}],
                }),
            });
            const data = await res.json();
            if (data.error) {
                setAiError("API error: " + data.error.message);
            } else {
                const text =
                    data.content?.map((c) => c.text || "").join("") ||
                    "No response received.";
                setAiSuggestion(text);
            }
        } catch (e) {
            setAiError("Error: " + (e.message || String(e)));
        }
        setAiLoading(false);
    };

    return (
        <div>
            <PH title="Badge Reservations" action={null}/>
            <p style={S.hint}>
                Reserve badge spots for companies before you know who's attending.
                Personal holds on individual guest cards also count against your total
                pool.
            </p>

            {/* Badge capacity overview */}
            <div style={{...S.card, marginBottom: 24, borderTop: `3px solid ${G}`}}>
                <div
                    style={{fontWeight: 700, fontSize: 14, color: G2, marginBottom: 14}}
                >
                    🎫 Badge Capacity Overview
                </div>
                <div style={{overflowX: "auto"}}>
                    <table style={{...S.table, minWidth: 400}}>
                        <thead>
                        <tr>
                            <th style={{...S.th, textAlign: "left"}}>Category</th>
                            {DAYS.map((d) => (
                                <th key={d.id} style={{...S.th, textAlign: "center"}}>
                                    {d.short}
                                </th>
                            ))}
                            <th style={{...S.th, textAlign: "center", color: G2}}>
                                TBD / Pool
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td style={{...S.td, color: "#7ecf7e", fontWeight: 600}}>
                                ✅ Confirmed guests
                            </td>
                            {DAYS.map((d) => (
                                <td
                                    key={d.id}
                                    style={{
                                        ...S.td,
                                        textAlign: "center",
                                        color: "#7ecf7e",
                                        fontWeight: 700,
                                    }}
                                >
                                    {guests.filter(
                                        (g) =>
                                            g.status === "confirmed" && g.badgeDays?.includes(d.id)
                                    ).length || "—"}
                                </td>
                            ))}
                            <td style={{...S.td, textAlign: "center", color: DIM}}>—</td>
                        </tr>
                        <tr>
                            <td style={{...S.td, color: G2, fontWeight: 600}}>
                                💌 Interested guests
                            </td>
                            {DAYS.map((d) => (
                                <td
                                    key={d.id}
                                    style={{
                                        ...S.td,
                                        textAlign: "center",
                                        color: G2,
                                        fontWeight: 700,
                                    }}
                                >
                                    {guests.filter(
                                        (g) =>
                                            g.status === "soft-invite" &&
                                            g.badgeDays?.includes(d.id)
                                    ).length || "—"}
                                </td>
                            ))}
                            <td style={{...S.td, textAlign: "center", color: DIM}}>—</td>
                        </tr>
                        <tr>
                            <td style={{...S.td, color: G, fontWeight: 600}}>
                                📌 Company reservations
                            </td>
                            {DAYS.map((d) => {
                                const tot = reservations
                                    .filter((r) => r.day === d.id)
                                    .reduce((s, r) => s + r.qty, 0);
                                return (
                                    <td
                                        key={d.id}
                                        style={{
                                            ...S.td,
                                            textAlign: "center",
                                            color: G,
                                            fontWeight: 700,
                                        }}
                                    >
                                        {tot || "—"}
                                    </td>
                                );
                            })}
                            <td
                                style={{
                                    ...S.td,
                                    textAlign: "center",
                                    color: G,
                                    fontWeight: 700,
                                }}
                            >
                                {tbdRes || "—"}
                            </td>
                        </tr>
                        <tr>
                            <td style={{...S.td, color: G, fontWeight: 600}}>
                                👤 Personal holds
                            </td>
                            {DAYS.map((d) => (
                                <td
                                    key={d.id}
                                    style={{...S.td, textAlign: "center", color: DIM}}
                                >
                                    —
                                </td>
                            ))}
                            <td
                                style={{
                                    ...S.td,
                                    textAlign: "center",
                                    color: G,
                                    fontWeight: 700,
                                }}
                            >
                                {holdCount || "—"}
                            </td>
                        </tr>
                        <tr
                            style={{
                                background: "rgba(201,168,76,0.08)",
                                borderTop: `2px solid ${BORDER}`,
                            }}
                        >
                            <td style={{...S.td, fontWeight: 700, color: WHITE}}>
                                Total committed
                            </td>
                            {DAYS.map((d) => {
                                const dayConfirmed = guests.filter(
                                    (g) =>
                                        g.status === "confirmed" && g.badgeDays?.includes(d.id)
                                ).length;
                                const dayInterested = guests.filter(
                                    (g) =>
                                        g.status === "soft-invite" && g.badgeDays?.includes(d.id)
                                ).length;
                                const dayRes = reservations
                                    .filter((r) => r.day === d.id)
                                    .reduce((s, r) => s + r.qty, 0);
                                const total = dayConfirmed + dayInterested + dayRes;
                                return (
                                    <td
                                        key={d.id}
                                        style={{
                                            ...S.td,
                                            textAlign: "center",
                                            fontWeight: 700,
                                            fontSize: 16,
                                            color: total > 0 ? WHITE : DIM,
                                        }}
                                    >
                                        {total || "—"}
                                    </td>
                                );
                            })}
                            <td
                                style={{
                                    ...S.td,
                                    textAlign: "center",
                                    fontWeight: 700,
                                    fontSize: 16,
                                    color: tbdRes + holdCount > 0 ? WHITE : DIM,
                                }}
                            >
                                {tbdRes + holdCount || "—"}
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
                {holdCount > 0 && (
                    <div
                        style={{
                            marginTop: 14,
                            borderTop: `1px solid ${BORDER}`,
                            paddingTop: 12,
                        }}
                    >
                        <div
                            style={{
                                fontSize: 11,
                                color: G,
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                marginBottom: 8,
                            }}
                        >
                            👤 Personal Badge Holds (day flexible)
                        </div>
                        <div style={{display: "flex", flexWrap: "wrap", gap: 6}}>
                            {personalHolds.map((g) => (
                                <span
                                    key={g.id}
                                    style={{
                                        ...S.chip,
                                        background: "rgba(201,168,76,0.15)",
                                        color: G2,
                                        border: `1px solid rgba(201,168,76,0.25)`,
                                    }}
                                >
                  {g.name || "Unnamed"}
                                    {g.company ? ` · ${g.company}` : ""}
                </span>
                            ))}
                        </div>
                    </div>
                )}
                <p style={{...S.hint, marginTop: 12, marginBottom: 0}}>
                    ⚠️ TBD column counts company reservations without a day assigned +
                    personal holds. These will consume badges from whichever day they land
                    on.
                </p>

                {/* AI Suggest button */}
                <div
                    style={{
                        marginTop: 16,
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        flexWrap: "wrap",
                    }}
                >
                    <button
                        onClick={getSuggestion}
                        disabled={aiLoading}
                        style={{
                            background: `linear-gradient(135deg,${G},${G2})`,
                            border: "none",
                            borderRadius: 8,
                            padding: "10px 20px",
                            fontWeight: 700,
                            fontSize: 14,
                            color: "#1a3a1a",
                            cursor: aiLoading ? "wait" : "pointer",
                            opacity: aiLoading ? 0.7 : 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontFamily: "Georgia,serif",
                        }}
                    >
                        {aiLoading ? "✨ Thinking…" : "✨ Suggest Allocation"}
                    </button>
                    {!settings.apiKey && (
                        <span style={{fontSize: 12, color: DIM}}>
              Add your Anthropic API key in Settings to enable this.
            </span>
                    )}
                    {aiSuggestion && (
                        <button
                            onClick={() => setAiSuggestion(null)}
                            style={{...S.btnXs}}
                        >
                            Clear
                        </button>
                    )}
                </div>

                {/* AI output */}
                {aiError && (
                    <div style={{marginTop: 12, color: "#e88070", fontSize: 13}}>
                        {aiError}
                    </div>
                )}
                {aiSuggestion && (
                    <div
                        style={{
                            marginTop: 16,
                            borderTop: `1px solid ${BORDER}`,
                            paddingTop: 16,
                        }}
                    >
                        <div
                            style={{
                                fontSize: 12,
                                color: G2,
                                fontWeight: 700,
                                marginBottom: 10,
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                            }}
                        >
                            ✨ AI Allocation Suggestion
                        </div>
                        <div
                            style={{
                                fontSize: 13,
                                color: WHITE,
                                lineHeight: 1.7,
                                whiteSpace: "pre-wrap",
                                fontFamily: "Georgia,serif",
                            }}
                        >
                            {aiSuggestion}
                        </div>
                    </div>
                )}
            </div>

            {/* Add reservation form */}
            <div style={{...S.card, marginBottom: 24}}>
                <div
                    style={{fontWeight: 700, fontSize: 14, color: G2, marginBottom: 14}}
                >
                    + New Reservation
                </div>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr auto auto auto",
                        gap: 10,
                        alignItems: "end",
                    }}
                >
                    <Field label="Company">
                        <div style={{position: "relative"}}>
                            <Inp
                                value={newCo}
                                onChange={(e) => setNewCo(e.target.value)}
                                placeholder="e.g. Safe-Guard"
                                list="company-suggestions"
                            />
                            <datalist id="company-suggestions">
                                {knownCompanies.map((c) => (
                                    <option key={c} value={c}/>
                                ))}
                            </datalist>
                        </div>
                    </Field>
                    <Field label="Day">
                        <Sel
                            value={newDay}
                            onChange={(e) => setNewDay(e.target.value)}
                            style={{width: 130}}
                        >
                            <option value="tbd">📅 Day TBD</option>
                            {DAYS.map((d) => (
                                <option key={d.id} value={d.id}>
                                    {d.label}
                                </option>
                            ))}
                        </Sel>
                    </Field>
                    <Field label="Spots">
                        <input
                            type="number"
                            min="1"
                            max="20"
                            style={{...S.input, width: 70, textAlign: "center"}}
                            value={newQty}
                            onChange={(e) => setNewQty(Number(e.target.value))}
                        />
                    </Field>
                    <div style={{paddingBottom: 1}}>
                        <Btn onClick={addReservation}>Reserve</Btn>
                    </div>
                </div>
            </div>

            {reservations.length === 0 && (
                <EmptyCard>
                    No reservations yet. Add one above to start holding badge spots for
                    companies.
                </EmptyCard>
            )}

            {/* Group by company */}
            {companies.map((company) => {
                const companyRes = reservations.filter((r) => r.company === company);
                const allGuests = guests.filter(
                    (g) =>
                        g.type === "client" &&
                        g.company?.toLowerCase().trim() === company.toLowerCase().trim()
                );
                return (
                    <div key={company} style={{...S.card, marginBottom: 16}}>
                        {/* Company header */}
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: 16,
                            }}
                        >
                            <div>
                                <div style={{fontWeight: 700, fontSize: 18, color: G2}}>
                                    {company}
                                </div>
                                <div style={{fontSize: 12, color: DIM, marginTop: 2}}>
                                    {allGuests.length} guest{allGuests.length !== 1 ? "s" : ""} in
                                    system
                                    {allGuests.length > 0 && (
                                        <>
                                            {" "}
                                            ·{" "}
                                            {
                                                allGuests.filter((g) => g.status === "confirmed").length
                                            }{" "}
                                            confirmed
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Per-day reservations */}
                        {companyRes.map((res) => {
                            const confirmed = getConfirmed(res);
                            const interested = getInterested(res);
                            const filled = confirmed.length;
                            const open = Math.max(0, res.qty - filled);
                            const over = filled > res.qty;
                            const pct =
                                res.qty > 0 ? Math.min((filled / res.qty) * 100, 100) : 0;

                            return (
                                <div
                                    key={res.id}
                                    style={{
                                        borderTop: `1px solid ${BORDER}`,
                                        paddingTop: 14,
                                        marginBottom: 14,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            flexWrap: "wrap",
                                            gap: 10,
                                            marginBottom: 12,
                                        }}
                                    >
                                        <div
                                            style={{display: "flex", alignItems: "center", gap: 12}}
                                        >
                                            <div
                                                style={{
                                                    fontWeight: 600,
                                                    fontSize: 15,
                                                    color: res.day === "tbd" ? G : WHITE,
                                                }}
                                            >
                                                {res.day === "tbd"
                                                    ? "📅 Day TBD"
                                                    : DAYS.find((d) => d.id === res.day)?.label}
                                            </div>
                                            {res.day !== "tbd" && (
                                                <span style={{fontSize: 11, color: DIM}}>
                          {DAYS.find((d) => d.id === res.day)?.tag}
                        </span>
                                            )}
                                            {res.day === "tbd" && (
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        color: G,
                                                        background: "rgba(201,168,76,0.15)",
                                                        padding: "1px 7px",
                                                        borderRadius: 4,
                                                    }}
                                                >
                          needs scheduling
                        </span>
                                            )}
                                            {/* Editable day + qty */}
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 6,
                                                }}
                                            >
                                                <span style={{fontSize: 11, color: DIM}}>Day:</span>
                                                <Sel
                                                    value={res.day}
                                                    onChange={(e) =>
                                                        updateReservation(res.id, {day: e.target.value})
                                                    }
                                                    style={{fontSize: 12, padding: "3px 6px"}}
                                                >
                                                    <option value="tbd">📅 TBD</option>
                                                    {DAYS.map((d) => (
                                                        <option key={d.id} value={d.id}>
                                                            {d.short}
                                                        </option>
                                                    ))}
                                                </Sel>
                                            </div>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 6,
                                                }}
                                            >
                                                <span style={{fontSize: 11, color: DIM}}>Spots:</span>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="20"
                                                    style={{
                                                        ...S.input,
                                                        width: 52,
                                                        padding: "3px 6px",
                                                        fontSize: 13,
                                                        textAlign: "center",
                                                    }}
                                                    value={res.qty}
                                                    onChange={(e) =>
                                                        updateReservation(res.id, {
                                                            qty: Math.max(1, Number(e.target.value)),
                                                        })
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <div
                                            style={{display: "flex", alignItems: "center", gap: 10}}
                                        >
                                            <div style={{textAlign: "center"}}>
                                                <div
                                                    style={{
                                                        fontSize: 20,
                                                        fontWeight: 700,
                                                        color: over ? RED : open === 0 ? G : GACC,
                                                    }}
                                                >
                                                    {over ? `+${filled - res.qty}` : open}
                                                </div>
                                                <div style={{fontSize: 10, color: DIM}}>
                                                    {over ? "over" : open === 0 ? "full" : "open"}
                                                </div>
                                            </div>
                                            <button
                                                style={{
                                                    ...S.btnXs,
                                                    color: "#e88070",
                                                    borderColor: "rgba(192,57,43,0.3)",
                                                }}
                                                onClick={() => removeReservation(res.id)}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>

                                    {/* Available days — shown for TBD reservations */}
                                    {res.day === "tbd" && (
                                        <div
                                            style={{
                                                padding: "10px 14px",
                                                background: "rgba(201,168,76,0.08)",
                                                borderRadius: 8,
                                                border: `1px solid rgba(201,168,76,0.2)`,
                                                marginBottom: 12,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontSize: 11,
                                                    color: G,
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.08em",
                                                    marginBottom: 8,
                                                }}
                                            >
                                                Available days{" "}
                                                <span
                                                    style={{
                                                        color: DIM,
                                                        textTransform: "none",
                                                        letterSpacing: 0,
                                                    }}
                                                >
                          (leave blank if any day works)
                        </span>
                                            </div>
                                            <div
                                                style={{display: "flex", gap: 16, flexWrap: "wrap"}}
                                            >
                                                {DAYS.map((d) => (
                                                    <label
                                                        key={d.id}
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 6,
                                                            fontSize: 13,
                                                            cursor: "pointer",
                                                            color: (res.availableDays || []).includes(d.id)
                                                                ? G2
                                                                : WHITE,
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={(res.availableDays || []).includes(d.id)}
                                                            onChange={(e) =>
                                                                updateReservation(res.id, {
                                                                    availableDays: e.target.checked
                                                                        ? [...(res.availableDays || []), d.id]
                                                                        : (res.availableDays || []).filter(
                                                                            (x) => x !== d.id
                                                                        ),
                                                                })
                                                            }
                                                        />
                                                        {d.label}
                                                    </label>
                                                ))}
                                            </div>
                                            <div style={{fontSize: 11, color: DIM, marginTop: 8}}>
                                                {!(res.availableDays || []).length
                                                    ? "⚡ Fully flexible — AI will suggest the best day"
                                                    : "📅 Available: " +
                                                    (res.availableDays || [])
                                                        .map((id) => DAYS.find((d) => d.id === id)?.label)
                                                        .join(", ")}
                                            </div>
                                        </div>
                                    )}

                                    {/* Progress bar - only show when day is assigned */}
                                    {res.day !== "tbd" && (
                                        <div style={{...S.progBg, height: 8, marginBottom: 10}}>
                                            <div
                                                style={{
                                                    ...S.progBar,
                                                    width: `${pct}%`,
                                                    background: over ? RED : pct === 100 ? G : GACC,
                                                }}
                                            />
                                        </div>
                                    )}

                                    {/* Matched guests */}
                                    <div style={{display: "flex", gap: 16, flexWrap: "wrap"}}>
                                        <div style={{flex: 1, minWidth: 200}}>
                                            <div
                                                style={{
                                                    fontSize: 10,
                                                    color: "#7ecf7e",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.08em",
                                                    marginBottom: 6,
                                                }}
                                            >
                                                ✅ Confirmed ({confirmed.length}/{res.qty})
                                            </div>
                                            {confirmed.length === 0 ? (
                                                <div
                                                    style={{
                                                        color: DIM,
                                                        fontSize: 12,
                                                        fontStyle: "italic",
                                                    }}
                                                >
                                                    No confirmed guests yet
                                                </div>
                                            ) : (
                                                confirmed.map((g) => (
                                                    <div
                                                        key={g.id}
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 6,
                                                            marginBottom: 4,
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                width: 6,
                                                                height: 6,
                                                                borderRadius: "50%",
                                                                background: "#7ecf7e",
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                        <span style={{fontSize: 13, color: WHITE}}>
                              {g.name}
                            </span>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                        {interested.length > 0 && (
                                            <div style={{flex: 1, minWidth: 200}}>
                                                <div
                                                    style={{
                                                        fontSize: 10,
                                                        color: G2,
                                                        textTransform: "uppercase",
                                                        letterSpacing: "0.08em",
                                                        marginBottom: 6,
                                                    }}
                                                >
                                                    💌 Interested ({interested.length})
                                                </div>
                                                {interested.map((g) => (
                                                    <div
                                                        key={g.id}
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 6,
                                                            marginBottom: 4,
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                width: 6,
                                                                height: 6,
                                                                borderRadius: "50%",
                                                                background: G,
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                        <span style={{fontSize: 13, color: DIM}}>
                              {g.name}
                            </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
}

// ─── Badge Tracker ────────────────────────────────────────────────────────────
function BadgeTracker({guests, settings, setSettings}) {
    const upd = (day, val) =>
        setSettings((s) => ({
            ...s,
            badgesByDay: {...s.badgesByDay, [day]: Number(val)},
        }));

    // Summary table data
    const summaryRows = DAYS.map((d) => ({
        day: d,
        alloc: Number(settings.badgesByDay[d.id] || 0),
        confirmed: guests.filter(
            (g) => g.status === "confirmed" && g.badgeDays?.includes(d.id)
        ),
        interested: guests.filter(
            (g) =>
                g.status !== "confirmed" &&
                g.status !== "declined" &&
                g.badgeDays?.includes(d.id)
        ),
        total: guests.filter((g) => g.badgeDays?.includes(d.id)),
    }));

    return (
        <div>
            <PH title="Badge Allocations" action={null}/>
            <p style={S.hint}>
                Set your allocated badge count per day. Badge days on guest cards
                reflect interest/preference — use the confirmed column to track firm
                assignments.
            </p>

            {/* Summary overview table */}
            <div style={{...S.card, marginBottom: 24}}>
                <div
                    style={{fontWeight: 700, fontSize: 14, color: G2, marginBottom: 14}}
                >
                    📊 Day-by-Day Summary
                </div>
                <div style={{overflowX: "auto"}}>
                    <table style={{...S.table, minWidth: 500}}>
                        <thead>
                        <tr>
                            <th style={S.th}>Day</th>
                            <th style={{...S.th, textAlign: "center"}}>Allocated</th>
                            <th style={{...S.th, textAlign: "center", color: "#7ecf7e"}}>
                                ✅ Confirmed
                            </th>
                            <th style={{...S.th, textAlign: "center", color: G2}}>
                                💌 Interested
                            </th>
                            <th style={{...S.th, textAlign: "center"}}>
                                Total Requesting
                            </th>
                            <th style={{...S.th, textAlign: "center"}}>Remaining</th>
                        </tr>
                        </thead>
                        <tbody>
                        {summaryRows.map(
                            ({day, alloc, confirmed, interested, total}) => {
                                const rem = alloc - confirmed.length;
                                const overSubscribed = total.length > alloc && alloc > 0;
                                return (
                                    <tr key={day.id}>
                                        <td style={{...S.td, fontWeight: 700, color: G2}}>
                                            {day.label}
                                            <br/>
                                            <span
                                                style={{fontSize: 11, color: DIM, fontWeight: 400}}
                                            >
                          {day.tag}
                        </span>
                                        </td>
                                        <td
                                            style={{...S.td, textAlign: "center", color: WHITE}}
                                        >
                                            {alloc || <span style={{color: DIM}}>—</span>}
                                        </td>
                                        <td style={{...S.td, textAlign: "center"}}>
                        <span
                            style={{
                                fontWeight: 700,
                                color: "#7ecf7e",
                                fontSize: 16,
                            }}
                        >
                          {confirmed.length}
                        </span>
                                        </td>
                                        <td style={{...S.td, textAlign: "center"}}>
                        <span
                            style={{fontWeight: 700, color: G2, fontSize: 16}}
                        >
                          {interested.length}
                        </span>
                                        </td>
                                        <td style={{...S.td, textAlign: "center"}}>
                        <span
                            style={{
                                fontWeight: 700,
                                color: overSubscribed ? RED : WHITE,
                                fontSize: 16,
                            }}
                        >
                          {total.length}
                        </span>
                                            {overSubscribed && (
                                                <span
                                                    style={{
                                                        fontSize: 10,
                                                        color: RED,
                                                        display: "block",
                                                    }}
                                                >
                            over by {total.length - alloc}
                          </span>
                                            )}
                                        </td>
                                        <td style={{...S.td, textAlign: "center"}}>
                        <span
                            style={{
                                fontWeight: 700,
                                fontSize: 16,
                                color: rem < 0 ? RED : rem === 0 ? G : GACC,
                            }}
                        >
                          {alloc > 0 ? (
                              rem
                          ) : (
                              <span style={{color: DIM}}>—</span>
                          )}
                        </span>
                                        </td>
                                    </tr>
                                );
                            }
                        )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Per-day detail cards */}
            {DAYS.map((d) => {
                const alloc = Number(settings.badgesByDay[d.id] || 0);
                const confirmed = guests.filter(
                    (g) => g.status === "confirmed" && g.badgeDays?.includes(d.id)
                );
                const interested = guests.filter(
                    (g) =>
                        g.status !== "confirmed" &&
                        g.status !== "declined" &&
                        g.badgeDays?.includes(d.id)
                );
                const rem = alloc - confirmed.length;
                return (
                    <div key={d.id} style={S.card}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: 12,
                                marginBottom: 16,
                            }}
                        >
                            <div>
                                <div style={{fontWeight: 700, fontSize: 18, color: G2}}>
                                    {d.label}
                                </div>
                                <div style={{fontSize: 13, color: DIM}}>{d.tag}</div>
                            </div>
                            <div style={{display: "flex", alignItems: "center", gap: 20}}>
                                <Field label="Allocated">
                                    <input
                                        type="number"
                                        min="0"
                                        style={{...S.input, width: 72, textAlign: "center"}}
                                        value={alloc}
                                        onChange={(e) => upd(d.id, e.target.value)}
                                    />
                                </Field>
                                <div style={{textAlign: "center"}}>
                                    <div
                                        style={{
                                            fontSize: 10,
                                            color: DIM,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.1em",
                                            marginBottom: 4,
                                        }}
                                    >
                                        Remaining
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 22,
                                            fontWeight: 700,
                                            color: rem < 0 ? RED : rem === 0 ? G : GACC,
                                        }}
                                    >
                                        {alloc > 0 ? rem : "—"}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Confirmed */}
                        <div style={{marginBottom: 12}}>
                            <div
                                style={{
                                    fontSize: 10,
                                    color: "#7ecf7e",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.1em",
                                    marginBottom: 6,
                                }}
                            >
                                ✅ Confirmed ({confirmed.length})
                            </div>
                            <div style={{display: "flex", flexWrap: "wrap", gap: 6}}>
                                {confirmed.length === 0 ? (
                                    <span style={{color: DIM, fontSize: 13}}>
                    None confirmed
                  </span>
                                ) : (
                                    confirmed.map((g) => (
                                        <span
                                            key={g.id}
                                            style={{
                                                ...S.chip,
                                                background: "rgba(74,158,74,0.2)",
                                                color: "#7ecf7e",
                                                border: "1px solid rgba(74,158,74,0.3)",
                                            }}
                                        >
                      {g.name || "?"}
                    </span>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Interested / soft invite */}
                        {interested.length > 0 && (
                            <div style={{marginBottom: 12}}>
                                <div
                                    style={{
                                        fontSize: 10,
                                        color: G2,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.1em",
                                        marginBottom: 6,
                                    }}
                                >
                                    💌 Interested / Soft Invite ({interested.length})
                                </div>
                                <div style={{display: "flex", flexWrap: "wrap", gap: 6}}>
                                    {interested.map((g) => (
                                        <span
                                            key={g.id}
                                            style={{
                                                ...S.chip,
                                                background: "rgba(201,168,76,0.15)",
                                                color: G2,
                                                border: "1px solid rgba(201,168,76,0.25)",
                                            }}
                                        >
                      {g.name || "?"}
                    </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Progress bar - confirmed vs allocated */}
                        <div style={{marginTop: 8}}>
                            <div style={{fontSize: 10, color: DIM, marginBottom: 4}}>
                                {confirmed.length} confirmed of {alloc} allocated
                            </div>
                            <div
                                style={{
                                    ...S.progBg,
                                    position: "relative",
                                    height: 10,
                                    borderRadius: 5,
                                }}
                            >
                                {/* Interested layer behind */}
                                <div
                                    style={{
                                        ...S.progBar,
                                        width: `${
                                            alloc > 0
                                                ? Math.min(
                                                    ((confirmed.length + interested.length) / alloc) *
                                                    100,
                                                    100
                                                )
                                                : 0
                                        }%`,
                                        background: "rgba(201,168,76,0.25)",
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        borderRadius: 5,
                                    }}
                                />
                                {/* Confirmed layer on top */}
                                <div
                                    style={{
                                        ...S.progBar,
                                        width: `${
                                            alloc > 0
                                                ? Math.min((confirmed.length / alloc) * 100, 100)
                                                : 0
                                        }%`,
                                        background: rem < 0 ? RED : GACC,
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        borderRadius: 5,
                                    }}
                                />
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    gap: 16,
                                    marginTop: 6,
                                    fontSize: 11,
                                    color: DIM,
                                }}
                            >
                                <span style={{color: GACC}}>■ Confirmed</span>
                                <span style={{color: "rgba(201,168,76,0.6)"}}>
                  ■ Interested
                </span>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ─── Room Planner ─────────────────────────────────────────────────────────────
function RoomPlanner({guests, settings, setSettings, updateGuest}) {
    const [adding, setAdding] = useState(false);
    const [nr, setNr] = useState({name: "", beds: "King", capacity: 2});
    const saveRoom = () => {
        setSettings((s) => ({
            ...s,
            rooms: [...s.rooms, {...nr, id: "r" + Date.now()}],
        }));
        setNr({name: "", beds: "King", capacity: 2});
        setAdding(false);
    };
    const confirmedGuests = guests.filter(
        (g) => g.status === "confirmed" && g.needsHousing !== false
    );
    const removeRoom = (id) => {
        setSettings((s) => ({...s, rooms: s.rooms.filter((r) => r.id !== id)}));
        guests
            .filter((g) => g.roomId === id)
            .forEach((g) => updateGuest(g.id, {roomId: ""}));
    };
    return (
        <div>
            <PH
                title="Room Assignments"
                action={<Btn onClick={() => setAdding(true)}>+ Add Room</Btn>}
            />
            <p style={S.hint}>
                Showing confirmed guests only for occupancy and grid view.
            </p>
            {adding && (
                <div style={{...S.card, borderColor: G, marginBottom: 16}}>
                    <div style={S.formGrid}>
                        <Field label="Room Name">
                            <Inp
                                value={nr.name}
                                onChange={(e) => setNr((r) => ({...r, name: e.target.value}))}
                                placeholder="Master Suite"
                            />
                        </Field>
                        <Field label="Bed Type">
                            <Inp
                                value={nr.beds}
                                onChange={(e) => setNr((r) => ({...r, beds: e.target.value}))}
                                placeholder="King, 2 Queens…"
                            />
                        </Field>
                        <Field label="Capacity">
                            <input
                                type="number"
                                style={{...S.input, width: 80}}
                                value={nr.capacity}
                                onChange={(e) =>
                                    setNr((r) => ({...r, capacity: Number(e.target.value)}))
                                }
                            />
                        </Field>
                    </div>
                    <div style={{display: "flex", gap: 8, marginTop: 8}}>
                        <Btn onClick={saveRoom}>Save Room</Btn>
                        <BtnSec onClick={() => setAdding(false)}>Cancel</BtnSec>
                    </div>
                </div>
            )}
            <div style={S.roomGrid}>
                {settings.rooms.map((room) => {
                    const occ = confirmedGuests.filter((g) => g.roomId === room.id);
                    const pct = (occ.length / room.capacity) * 100;
                    return (
                        <div key={room.id} style={S.roomCard}>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                }}
                            >
                                <div>
                                    <div style={{fontWeight: 700, fontSize: 16, color: G2}}>
                                        {room.name}
                                    </div>
                                    <div style={{fontSize: 12, color: DIM, marginTop: 2}}>
                                        {room.beds} · Cap {room.capacity}
                                    </div>
                                </div>
                                <button style={S.btnXs} onClick={() => removeRoom(room.id)}>
                                    ✕
                                </button>
                            </div>
                            <div style={{...S.progBg, marginTop: 10}}>
                                <div
                                    style={{
                                        ...S.progBar,
                                        width: `${Math.min(pct, 100)}%`,
                                        background: pct > 100 ? RED : G,
                                    }}
                                />
                            </div>
                            <div style={{marginTop: 12}}>
                                {occ.length === 0 ? (
                                    <div style={{color: DIM, fontSize: 13}}>Vacant</div>
                                ) : (
                                    occ.map((g) => (
                                        <div
                                            key={g.id}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 8,
                                                marginTop: 8,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: 7,
                                                    height: 7,
                                                    borderRadius: "50%",
                                                    background: g.type === "host" ? GACC : G,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <div>
                                                <div style={{fontSize: 14, fontWeight: 600}}>
                                                    {g.name || "Unnamed"}
                                                </div>
                                                <div style={{fontSize: 11, color: DIM}}>
                                                    {DAYS.find((d) => d.id === g.arrivalDay)?.short} →{" "}
                                                    {DAYS.find((d) => d.id === g.departureDay)?.short}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div
                style={{fontWeight: 700, fontSize: 16, color: G2, marginBottom: 12}}
            >
                Weekly Grid
            </div>
            <div style={{...S.card, overflowX: "auto"}}>
                <table style={S.table}>
                    <thead>
                    <tr>
                        <th style={S.th}>Room</th>
                        {DAYS.map((d) => (
                            <th key={d.id} style={S.th}>
                                {d.short}
                            </th>
                        ))}
                    </tr>
                    </thead>
                    <tbody>
                    {settings.rooms.map((room) => (
                        <tr key={room.id}>
                            <td style={{...S.td, fontWeight: 600, color: G2}}>
                                {room.name}
                            </td>
                            {DAYS.map((d) => {
                                const occ = confirmedGuests.filter(
                                    (g) =>
                                        g.roomId === room.id &&
                                        daysInRange(g.arrivalDay, g.departureDay).includes(d.id)
                                );
                                return (
                                    <td
                                        key={d.id}
                                        style={{
                                            ...S.td,
                                            background: occ.length
                                                ? "rgba(201,168,76,0.1)"
                                                : "transparent",
                                        }}
                                    >
                                        {occ.map((g) => (
                                            <div
                                                key={g.id}
                                                style={{fontSize: 12, fontWeight: 600, color: G2}}
                                            >
                                                {g.name?.split(" ")[0] || "?"}
                                            </div>
                                        ))}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─── Meal Planner ─────────────────────────────────────────────────────────────
function MealPlanner({guests, updateGuest}) {
    const confirmedGuests = guests.filter((g) => g.status === "confirmed");
    return (
        <div>
            <PH title="Meal Schedule" action={null}/>
            <p style={S.hint}>
                Showing confirmed guests only. Track which guests are eating in-house
                for each meal slot.
            </p>
            <div style={{...S.card, overflowX: "auto"}}>
                <table style={S.table}>
                    <thead>
                    <tr>
                        <th style={S.th}>Guest</th>
                        {MEAL_SLOTS.map((m) => (
                            <th
                                key={m}
                                style={{
                                    ...S.th,
                                    writingMode: "vertical-rl",
                                    fontSize: 11,
                                    paddingBottom: 10,
                                }}
                            >
                                {m}
                            </th>
                        ))}
                    </tr>
                    </thead>
                    <tbody>
                    {confirmedGuests.map((g) => (
                        <tr key={g.id}>
                            <td
                                style={{
                                    ...S.td,
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                    color: G2,
                                }}
                            >
                                {g.name || "Unnamed"}
                            </td>
                            {MEAL_SLOTS.map((m) => (
                                <td key={m} style={{...S.td, textAlign: "center"}}>
                                    <input
                                        type="checkbox"
                                        checked={g.mealDays?.includes(m)}
                                        onChange={(e) =>
                                            updateGuest(g.id, {
                                                mealDays: e.target.checked
                                                    ? [...(g.mealDays || []), m]
                                                    : (g.mealDays || []).filter((x) => x !== m),
                                            })
                                        }
                                    />
                                </td>
                            ))}
                        </tr>
                    ))}
                    {confirmedGuests.length === 0 && (
                        <tr>
                            <td style={S.td} colSpan={MEAL_SLOTS.length + 1}>
                                <EmptyState>No confirmed guests yet.</EmptyState>
                            </td>
                        </tr>
                    )}
                    <tr style={{background: "rgba(201,168,76,0.08)"}}>
                        <td style={{...S.td, fontWeight: 700, color: G}}>Headcount</td>
                        {MEAL_SLOTS.map((m) => (
                            <td
                                key={m}
                                style={{
                                    ...S.td,
                                    textAlign: "center",
                                    color: G,
                                    fontWeight: 700,
                                }}
                            >
                                {confirmedGuests.filter((g) => g.mealDays?.includes(m))
                                    .length || "—"}
                            </td>
                        ))}
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─── Swag Tracker ─────────────────────────────────────────────────────────────
function SwagTracker({guests, updateGuest, settings, setSettings}) {
    const swagItems = settings.swagItems || [
        "Hat",
        "Shirt",
        "Jacket",
        "Bag",
        "Umbrella",
        "Cooler",
        "Yeti",
        "Other",
    ];
    const confirmedGuests = guests.filter((g) => g.status === "confirmed");
    const [newItem, setNewItem] = useState("");
    const [editingIdx, setEditingIdx] = useState(null);
    const [editVal, setEditVal] = useState("");

    const updSwagItems = (items) =>
        setSettings((s) => ({...s, swagItems: items}));
    const addItem = () => {
        const t = newItem.trim();
        if (!t) return;
        updSwagItems([...swagItems, t]);
        setNewItem("");
    };
    const removeItem = (idx) =>
        updSwagItems(swagItems.filter((_, i) => i !== idx));
    const startEdit = (idx) => {
        setEditingIdx(idx);
        setEditVal(swagItems[idx]);
    };
    const saveEdit = () => {
        const t = editVal.trim();
        if (t) updSwagItems(swagItems.map((s, i) => (i === editingIdx ? t : s)));
        setEditingIdx(null);
    };

    return (
        <div>
            <PH title="Swag Tracker" action={null}/>
            <p style={S.hint}>Showing confirmed guests only.</p>

            {/* Swag item editor */}
            <div style={{...S.card, marginBottom: 16}}>
                <div
                    style={{fontWeight: 700, fontSize: 14, color: G2, marginBottom: 10}}
                >
                    🎁 Swag Items
                </div>
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                        marginBottom: 12,
                    }}
                >
                    {swagItems.map((item, idx) => (
                        <div
                            key={idx}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                                background: "rgba(201,168,76,0.12)",
                                border: `1px solid rgba(201,168,76,0.25)`,
                                borderRadius: 8,
                                padding: "4px 10px",
                            }}
                        >
                            {editingIdx === idx ? (
                                <input
                                    autoFocus
                                    style={{
                                        ...S.input,
                                        width: 90,
                                        padding: "2px 6px",
                                        fontSize: 13,
                                    }}
                                    value={editVal}
                                    onChange={(e) => setEditVal(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") saveEdit();
                                        if (e.key === "Escape") setEditingIdx(null);
                                    }}
                                    onBlur={saveEdit}
                                />
                            ) : (
                                <span
                                    style={{fontSize: 13, color: G2, cursor: "pointer"}}
                                    onClick={() => startEdit(idx)}
                                >
                  {item}
                </span>
                            )}
                            <button
                                style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    color: DIM,
                                    fontSize: 15,
                                    lineHeight: 1,
                                    padding: "0 2px",
                                }}
                                onClick={() => removeItem(idx)}
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
                <div style={{display: "flex", gap: 8}}>
                    <Inp
                        style={{flex: 1}}
                        placeholder="Add new swag item…"
                        value={newItem}
                        onChange={(e) => setNewItem(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && addItem()}
                    />
                    <Btn onClick={addItem} style={{padding: "8px 14px"}}>
                        +
                    </Btn>
                </div>
                <p style={{...S.hint, marginTop: 8, marginBottom: 0}}>
                    Click any item name to rename it. Changes update the tracker table
                    instantly.
                </p>
            </div>

            {/* Swag grid */}
            <div style={{...S.card, overflowX: "auto"}}>
                <table style={S.table}>
                    <thead>
                    <tr>
                        <th style={S.th}>Guest</th>
                        {swagItems.map((s) => (
                            <th
                                key={s}
                                style={{
                                    ...S.th,
                                    writingMode: "vertical-rl",
                                    fontSize: 11,
                                    paddingBottom: 10,
                                }}
                            >
                                {s}
                            </th>
                        ))}
                        <th style={S.th}>Total</th>
                    </tr>
                    </thead>
                    <tbody>
                    {confirmedGuests.map((g) => {
                        const total = swagItems.filter((s) => g.swag?.[s]).length;
                        return (
                            <tr key={g.id}>
                                <td
                                    style={{
                                        ...S.td,
                                        fontWeight: 600,
                                        whiteSpace: "nowrap",
                                        color: G2,
                                    }}
                                >
                                    {g.name || "Unnamed"}
                                </td>
                                {swagItems.map((s) => (
                                    <td key={s} style={{...S.td, textAlign: "center"}}>
                                        <input
                                            type="checkbox"
                                            checked={!!g.swag?.[s]}
                                            onChange={(e) =>
                                                updateGuest(g.id, {
                                                    swag: {...g.swag, [s]: e.target.checked},
                                                })
                                            }
                                        />
                                    </td>
                                ))}
                                <td
                                    style={{
                                        ...S.td,
                                        textAlign: "center",
                                        fontWeight: 700,
                                        color: G,
                                    }}
                                >
                                    {total}
                                </td>
                            </tr>
                        );
                    })}
                    {confirmedGuests.length === 0 && (
                        <tr>
                            <td colSpan={swagItems.length + 2} style={S.td}>
                                <EmptyState>No confirmed guests yet.</EmptyState>
                            </td>
                        </tr>
                    )}
                    <tr style={{background: "rgba(201,168,76,0.08)"}}>
                        <td style={{...S.td, fontWeight: 700, color: G}}>
                            Total Needed
                        </td>
                        {swagItems.map((s) => (
                            <td
                                key={s}
                                style={{
                                    ...S.td,
                                    textAlign: "center",
                                    color: G,
                                    fontWeight: 700,
                                }}
                            >
                                {confirmedGuests.filter((g) => g.swag?.[s]).length || "—"}
                            </td>
                        ))}
                        <td style={S.td}/>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─── Grocery Planner ──────────────────────────────────────────────────────────
function GroceryPlanner({groceries, setGroceries, guests}) {
    const [activeTab, setActiveTab] = useState("food");
    const [newItems, setNewItems] = useState({});
    const [newQtys, setNewQtys] = useState({});
    const updCat = (catId, items) =>
        setGroceries((g) =>
            g.map((c) => (c.categoryId === catId ? {...c, items} : c))
        );
    const addItem = (catId) => {
        const text = (newItems[catId] || "").trim();
        if (!text) return;
        const cat = groceries.find((c) => c.categoryId === catId);
        updCat(catId, [
            ...(cat?.items || []),
            {
                id: Date.now().toString(),
                name: text,
                qty: newQtys[catId] || "",
                checked: false,
                note: "",
            },
        ]);
        setNewItems((n) => ({...n, [catId]: ""}));
        setNewQtys((n) => ({...n, [catId]: ""}));
    };
    const toggleItem = (catId, id) => {
        const c = groceries.find((x) => x.categoryId === catId);
        updCat(
            catId,
            c.items.map((i) => (i.id === id ? {...i, checked: !i.checked} : i))
        );
    };
    const removeItem = (catId, id) => {
        const c = groceries.find((x) => x.categoryId === catId);
        updCat(
            catId,
            c.items.filter((i) => i.id !== id)
        );
    };
    const updateItem = (catId, id, patch) => {
        const c = groceries.find((x) => x.categoryId === catId);
        updCat(
            catId,
            c.items.map((i) => (i.id === id ? {...i, ...patch} : i))
        );
    };
    const foodCats = GROCERY_CATEGORIES.filter(
        (c) => !["beer", "wine", "nonalc", "supplies"].includes(c.id)
    );
    const drinkCats = GROCERY_CATEGORIES.filter((c) =>
        ["beer", "wine", "nonalc"].includes(c.id)
    );
    const supplyCats = GROCERY_CATEGORIES.filter((c) => c.id === "supplies");
    const displayCats =
        activeTab === "food"
            ? foodCats
            : activeTab === "drinks"
                ? drinkCats
                : supplyCats;
    const totItems = groceries.reduce((s, c) => s + c.items.length, 0);
    const chkItems = groceries.reduce(
        (s, c) => s + c.items.filter((i) => i.checked).length,
        0
    );
    return (
        <div>
            <PH title="Food & Grocery" action={null}/>
            {totItems > 0 && (
                <div
                    style={{
                        ...S.card,
                        padding: "12px 20px",
                        marginBottom: 16,
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                    }}
                >
                    <div style={S.progBg}>
                        <div
                            style={{
                                ...S.progBar,
                                width: `${Math.round((chkItems / totItems) * 100)}%`,
                                background: chkItems === totItems ? GACC : G,
                            }}
                        />
                    </div>
                    <span style={{fontSize: 13, whiteSpace: "nowrap", color: DIM}}>
            {chkItems} of {totItems} items gathered
          </span>
                </div>
            )}
            <FilterRow
                options={[
                    ["food", "🍳 Food"],
                    ["drinks", "🍷 Drinks"],
                    ["supplies", "🧴 Supplies"],
                ]}
                value={activeTab}
                onChange={setActiveTab}
            />
            {displayCats.map((cat) => {
                const catData = groceries.find((c) => c.categoryId === cat.id) || {
                    items: [],
                };
                const unchecked = catData.items.filter((i) => !i.checked),
                    checked = catData.items.filter((i) => i.checked);
                return (
                    <div key={cat.id} style={S.card}>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 12,
                            }}
                        >
              <span style={{fontWeight: 700, fontSize: 16, color: G2}}>
                {cat.label}
              </span>
                            <span style={{fontSize: 12, color: DIM}}>
                {catData.items.length} items
              </span>
                        </div>
                        <div style={{display: "flex", gap: 8, marginBottom: 12}}>
                            <Inp
                                style={{flex: 3}}
                                placeholder="Add item…"
                                value={newItems[cat.id] || ""}
                                onChange={(e) =>
                                    setNewItems((n) => ({...n, [cat.id]: e.target.value}))
                                }
                                onKeyDown={(e) => e.key === "Enter" && addItem(cat.id)}
                            />
                            <Inp
                                style={{flex: 1}}
                                placeholder="Qty"
                                value={newQtys[cat.id] || ""}
                                onChange={(e) =>
                                    setNewQtys((n) => ({...n, [cat.id]: e.target.value}))
                                }
                                onKeyDown={(e) => e.key === "Enter" && addItem(cat.id)}
                            />
                            <Btn
                                onClick={() => addItem(cat.id)}
                                style={{padding: "8px 14px"}}
                            >
                                +
                            </Btn>
                        </div>
                        {unchecked.map((item) => (
                            <GrocItem
                                key={item.id}
                                item={item}
                                onToggle={() => toggleItem(cat.id, item.id)}
                                onRemove={() => removeItem(cat.id, item.id)}
                                onUpdate={(p) => updateItem(cat.id, item.id, p)}
                            />
                        ))}
                        {checked.length > 0 && (
                            <div
                                style={{
                                    marginTop: 8,
                                    borderTop: `1px dashed ${BORDER}`,
                                    paddingTop: 8,
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 11,
                                        color: DIM,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.06em",
                                        marginBottom: 6,
                                    }}
                                >
                                    Gathered
                                </div>
                                {checked.map((item) => (
                                    <GrocItem
                                        key={item.id}
                                        item={item}
                                        faded
                                        onToggle={() => toggleItem(cat.id, item.id)}
                                        onRemove={() => removeItem(cat.id, item.id)}
                                        onUpdate={(p) => updateItem(cat.id, item.id, p)}
                                    />
                                ))}
                            </div>
                        )}
                        {catData.items.length === 0 && (
                            <EmptyState>No items yet. Type above and press Enter.</EmptyState>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function GrocItem({item, onToggle, onRemove, onUpdate, faded = false}) {
    const [editNote, setEditNote] = useState(false);
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "7px 0",
                borderBottom: `1px solid ${BORDER}`,
                opacity: faded ? 0.5 : 1,
            }}
        >
            <input
                type="checkbox"
                checked={item.checked}
                onChange={onToggle}
                style={{cursor: "pointer", flexShrink: 0}}
            />
            <span
                style={{
                    flex: 1,
                    textDecoration: item.checked ? "line-through" : "none",
                    fontSize: 14,
                    color: WHITE,
                }}
            >
        {item.name}
      </span>
            {item.qty && <span style={S.chip}>{item.qty}</span>}
            {editNote ? (
                <input
                    autoFocus
                    style={{...S.input, width: 130, fontSize: 12}}
                    value={item.note}
                    onChange={(e) => onUpdate({note: e.target.value})}
                    onBlur={() => setEditNote(false)}
                    placeholder="Note…"
                />
            ) : (
                <button
                    style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: 11,
                        color: item.note ? G : DIM,
                        padding: "2px 4px",
                    }}
                    onClick={() => setEditNote(true)}
                >
                    {item.note || "note"}
                </button>
            )}
            <button
                style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: DIM,
                    fontSize: 18,
                    lineHeight: 1,
                    padding: "0 4px",
                }}
                onClick={onRemove}
            >
                ×
            </button>
        </div>
    );
}

// ─── Cleaning Schedule ────────────────────────────────────────────────────────
function CleaningSchedule({cleaning, setCleaning, guests}) {
    const [newTasks, setNewTasks] = useState({});
    const [newAsgn, setNewAsgn] = useState({});
    const hosts = guests.filter((g) => g.type === "host");
    const updSlot = (id, tasks) =>
        setCleaning((c) => c.map((s) => (s.slotId === id ? {...s, tasks} : s)));
    const addTask = (id) => {
        const text = (newTasks[id] || "").trim();
        if (!text) return;
        const sl = cleaning.find((s) => s.slotId === id);
        updSlot(id, [
            ...(sl?.tasks || []),
            {
                id: Date.now().toString(),
                label: text,
                done: false,
                assignee: newAsgn[id] || "",
            },
        ]);
        setNewTasks((n) => ({...n, [id]: ""}));
        setNewAsgn((n) => ({...n, [id]: ""}));
    };
    const addTemplate = (id, label) => {
        const sl = cleaning.find((s) => s.slotId === id);
        if (sl?.tasks.some((t) => t.label === label)) return;
        updSlot(id, [
            ...(sl?.tasks || []),
            {id: Date.now().toString(), label, done: false, assignee: ""},
        ]);
    };
    const toggleTask = (id, tid) => {
        const s = cleaning.find((x) => x.slotId === id);
        updSlot(
            id,
            s.tasks.map((t) => (t.id === tid ? {...t, done: !t.done} : t))
        );
    };
    const removeTask = (id, tid) => {
        const s = cleaning.find((x) => x.slotId === id);
        updSlot(
            id,
            s.tasks.filter((t) => t.id !== tid)
        );
    };
    const updateTask = (id, tid, patch) => {
        const s = cleaning.find((x) => x.slotId === id);
        updSlot(
            id,
            s.tasks.map((t) => (t.id === tid ? {...t, ...patch} : t))
        );
    };
    const totTasks = cleaning.reduce((s, sl) => s + sl.tasks.length, 0);
    const doneTasks = cleaning.reduce(
        (s, sl) => s + sl.tasks.filter((t) => t.done).length,
        0
    );
    return (
        <div>
            <PH title="Cleaning Schedule" action={null}/>
            {totTasks > 0 && (
                <div
                    style={{
                        ...S.card,
                        padding: "12px 20px",
                        marginBottom: 16,
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                    }}
                >
                    <div style={S.progBg}>
                        <div
                            style={{
                                ...S.progBar,
                                width: `${Math.round((doneTasks / totTasks) * 100)}%`,
                                background: doneTasks === totTasks ? GACC : "#4a9e4a",
                            }}
                        />
                    </div>
                    <span style={{fontSize: 13, whiteSpace: "nowrap", color: DIM}}>
            {doneTasks} of {totTasks} tasks complete
          </span>
                </div>
            )}
            <div style={S.card}>
                <div
                    style={{
                        fontSize: 11,
                        color: DIM,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        marginBottom: 10,
                    }}
                >
                    Quick-add common tasks
                </div>
                <div style={{display: "flex", flexWrap: "wrap", gap: 6}}>
                    {CLEANING_TASK_TEMPLATES.map((t) => (
                        <span
                            key={t}
                            style={{...S.chip, cursor: "pointer"}}
                            className="template-pill"
                            onClick={() => {
                                const target =
                                    cleaning.find((s) => !s.tasks.some((tk) => tk.label === t)) ||
                                    cleaning[0];
                                if (target) addTemplate(target.slotId, t);
                            }}
                        >
              {t}
            </span>
                    ))}
                </div>
                <p style={{...S.hint, marginTop: 8}}>
                    Click a chip to add it to the first available slot.
                </p>
            </div>
            {CLEANING_SLOTS.map((slot) => {
                const slotData = cleaning.find((s) => s.slotId === slot.id) || {
                    tasks: [],
                };
                const done = slotData.tasks.filter((t) => t.done).length,
                    total = slotData.tasks.length;
                return (
                    <div
                        key={slot.id}
                        style={{
                            ...S.card,
                            borderLeft: `3px solid ${
                                slot.id.endsWith("-morn") || slot.id === "wed-eve" ? G : BORDER
                            }`,
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 12,
                            }}
                        >
              <span style={{fontWeight: 700, fontSize: 15, color: G2}}>
                {slot.label}
              </span>
                            {total > 0 && (
                                <span
                                    style={{fontSize: 12, color: done === total ? GACC : DIM}}
                                >
                  {done === total ? "✓ Complete" : `${done}/${total}`}
                </span>
                            )}
                        </div>
                        {slotData.tasks.map((task) => (
                            <div
                                key={task.id}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "7px 0",
                                    borderBottom: `1px solid ${BORDER}`,
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={task.done}
                                    onChange={() => toggleTask(slot.id, task.id)}
                                    style={{cursor: "pointer", flexShrink: 0}}
                                />
                                <span
                                    style={{
                                        flex: 1,
                                        textDecoration: task.done ? "line-through" : "none",
                                        fontSize: 14,
                                        opacity: task.done ? 0.5 : 1,
                                        color: WHITE,
                                    }}
                                >
                  {task.label}
                </span>
                                <Sel
                                    value={task.assignee}
                                    onChange={(e) =>
                                        updateTask(slot.id, task.id, {assignee: e.target.value})
                                    }
                                    style={{width: 130, fontSize: 12, padding: "4px 6px"}}
                                >
                                    <option value="">Unassigned</option>
                                    {hosts.map((h) => (
                                        <option key={h.id} value={h.name}>
                                            {h.name}
                                        </option>
                                    ))}
                                    <option value="Staff">Staff</option>
                                    <option value="Cleaning Service">Cleaning Service</option>
                                </Sel>
                                {task.assignee && (
                                    <span
                                        style={{
                                            ...S.chip,
                                            background: "rgba(74,158,74,0.2)",
                                            color: "#7ecf7e",
                                            fontSize: 11,
                                            flexShrink: 0,
                                        }}
                                    >
                    {task.assignee}
                  </span>
                                )}
                                <button
                                    style={{
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        color: DIM,
                                        fontSize: 18,
                                        lineHeight: 1,
                                        padding: "0 4px",
                                    }}
                                    onClick={() => removeTask(slot.id, task.id)}
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                        {slotData.tasks.length === 0 && (
                            <EmptyState>No tasks yet.</EmptyState>
                        )}
                        <div style={{display: "flex", gap: 8, marginTop: 12}}>
                            <Inp
                                style={{flex: 3}}
                                placeholder="Add task…"
                                value={newTasks[slot.id] || ""}
                                onChange={(e) =>
                                    setNewTasks((n) => ({...n, [slot.id]: e.target.value}))
                                }
                                onKeyDown={(e) => e.key === "Enter" && addTask(slot.id)}
                            />
                            <Sel
                                style={{flex: 2}}
                                value={newAsgn[slot.id] || ""}
                                onChange={(e) =>
                                    setNewAsgn((n) => ({...n, [slot.id]: e.target.value}))
                                }
                            >
                                <option value="">Assignee (optional)</option>
                                {hosts.map((h) => (
                                    <option key={h.id} value={h.name}>
                                        {h.name}
                                    </option>
                                ))}
                                <option value="Staff">Staff</option>
                                <option value="Cleaning Service">Cleaning Service</option>
                            </Sel>
                            <Btn
                                onClick={() => addTask(slot.id)}
                                style={{padding: "8px 14px"}}
                            >
                                +
                            </Btn>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ─── Comm Templates ───────────────────────────────────────────────────────────
function CommTemplates({guests, settings}) {
    const [which, setWhich] = useState("guest-invite");
    const [sel, setSel] = useState("");
    const guest = guests.find((g) => g.id === sel);
    const templates = {
        "guest-invite": {
            label: "Guest Invitation",
            subject: `You're Invited – ${settings.companyName} Masters ${settings.eventYear}`,
            body: `Dear [Guest Name],\n\nWe are thrilled to invite you to join us at the ${settings.companyName} Masters ${settings.eventYear} hospitality experience at ${settings.houseName}.\n\nThis year, we will be hosting a select group of guests at our private residence just minutes from Augusta National Golf Club. We have planned an exceptional week with access to the tournament, world-class dining, and an atmosphere you won't soon forget.\n\nYour Details:\n• Arrival: [Arrival Day]\n• Departure: [Departure Day]\n• Badge Days: [Badge Days]\n• Accommodation: ${settings.houseName}, ${settings.houseAddress}\n\nIn the coming weeks, you will receive a full itinerary and logistics guide. Please don't hesitate to reach out with any questions.\n\nWe look forward to welcoming you to Augusta.\n\nWarm regards,\n[Your Name]\n${settings.companyName}`,
        },
        "guest-logistics": {
            label: "Logistics Guide",
            subject: `Masters ${settings.eventYear} – Your Logistics Guide`,
            body: `Dear [Guest Name],\n\nThe Masters is just around the corner. Here is your complete logistics guide.\n\n━━━ YOUR STAY ━━━\n${settings.houseName}\n${settings.houseAddress}\n\nCheck-in: [Arrival Day] after 3:00 PM\nCheck-out: [Departure Day] by 11:00 AM\nYour Room: [Room Name]\n\n━━━ BADGE INFORMATION ━━━\nYour badge is valid for: [Badge Days]\n\nPlease bring a valid government-issued ID. No phones with cameras beyond the gates. Bags must be 9"×9"×14" or smaller.\n\n━━━ DINING ━━━\nAll meals at the house are provided. Notify us of any dietary needs.\n\n━━━ TRANSPORTATION ━━━\nGroup transportation departs daily. Please be ready 30 minutes before your tee time window.\n\n━━━ WHAT TO BRING ━━━\n• Comfortable walking shoes\n• Layers (spring mornings can be cool)\n• Cash only (no credit cards on grounds)\n• Your badge — non-replaceable\n\n━━━ YOUR HOST ━━━\n[Host Name] · [Host Phone]\n\nWarmly,\n[Your Name]\n${settings.companyName}`,
        },
        "host-brief": {
            label: "Host Briefing",
            subject: `[INTERNAL] Masters ${settings.eventYear} Host Briefing`,
            body: `INTERNAL — ${settings.companyName} Masters ${
                settings.eventYear
            }\nConfidential — Do Not Forward\n\n━━━ OVERVIEW ━━━\nVenue: ${
                settings.houseName
            }, ${settings.houseAddress}\nClients: ${
                guests.filter((g) => g.type === "client").length
            } · Hosts: ${
                guests.filter((g) => g.type === "host").length
            }\n\n━━━ GUEST ROSTER ━━━\n${
                guests
                    .filter((g) => g.type === "client")
                    .map(
                        (g) =>
                            `• ${g.name || "TBD"} (${g.company || "—"}) | Arrives: ${
                                DAYS.find((d) => d.id === g.arrivalDay)?.label
                            } | Departs: ${
                                DAYS.find((d) => d.id === g.departureDay)?.label
                            } | Room: ${
                                settings.rooms.find((r) => r.id === g.roomId)?.name || "TBD"
                            } | Badges: ${
                                g.badgeDays
                                    ?.map((d) => DAYS.find((x) => x.id === d)?.short)
                                    .join(", ") || "None"
                            }`
                    )
                    .join("\n") || "No clients yet."
            }\n\n━━━ DAILY BADGE COUNTS ━━━\n${DAYS.map(
                (d) =>
                    `${d.label} (${d.tag}): ${Number(
                        settings.badgesByDay[d.id] || 0
                    )} allocated | ${
                        guests.filter((g) => g.badgeDays?.includes(d.id)).length
                    } assigned`
            ).join(
                "\n"
            )}\n\n━━━ HOST RESPONSIBILITIES ━━━\n• Greet guests on arrival\n• Be primary point of contact for assigned guests\n• Coordinate badge pickup and transportation\n• Communicate dietary needs to catering\n• Distribute swag on first day of each guest's stay\n• Collect feedback for post-event debrief\n\n${
                settings.companyName
            } Event Team`,
        },
        "thank-you": {
            label: "Thank You",
            subject: `Thank You – ${settings.companyName} Masters ${settings.eventYear}`,
            body: `Dear [Guest Name],\n\nAs the roars from Amen Corner have faded and the azaleas rest for another year, I wanted to personally thank you for joining us at ${settings.houseName} during Masters ${settings.eventYear}.\n\nHaving you with us made the week truly special. I hope the memories — the golf, the conversations, the shared moments — stay with you as long as they will with us.\n\nWith gratitude,\n[Your Name]\n${settings.companyName}`,
        },
    };
    const t = templates[which];
    const fill = (text) => {
        if (!guest) return text;
        const bdDays =
            (guest.badgeDays || [])
                .map((d) => DAYS.find((x) => x.id === d)?.label)
                .join(", ") || "None";
        const room =
            settings.rooms.find((r) => r.id === guest.roomId)?.name || "TBD";
        return text
            .replace(/\[Guest Name\]/g, guest.name || "[Guest Name]")
            .replace(
                /\[Arrival Day\]/g,
                DAYS.find((d) => d.id === guest.arrivalDay)?.label || ""
            )
            .replace(
                /\[Departure Day\]/g,
                DAYS.find((d) => d.id === guest.departureDay)?.label || ""
            )
            .replace(/\[Badge Days\]/g, bdDays)
            .replace(/\[Room Name\]/g, room);
    };
    return (
        <div>
            <PH title="Communication Templates" action={null}/>
            <FilterRow
                options={Object.entries(templates).map(([k, v]) => [k, v.label])}
                value={which}
                onChange={setWhich}
            />
            <div style={{...S.card, marginBottom: 12}}>
                <Field label="Auto-fill for Guest (optional)">
                    <Sel value={sel} onChange={(e) => setSel(e.target.value)}>
                        <option value="">— Generic Template —</option>
                        {guests
                            .filter((g) => g.type === "client")
                            .map((g) => (
                                <option key={g.id} value={g.id}>
                                    {g.name || "Unnamed"}
                                </option>
                            ))}
                    </Sel>
                </Field>
            </div>
            <div style={S.card}>
                <div
                    style={{
                        fontSize: 10,
                        color: DIM,
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                        marginBottom: 6,
                    }}
                >
                    Subject
                </div>
                <div
                    style={{fontWeight: 700, fontSize: 16, color: G2, marginBottom: 16}}
                >
                    {fill(t.subject)}
                </div>
                <div
                    style={{
                        fontSize: 10,
                        color: DIM,
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                        marginBottom: 6,
                    }}
                >
                    Body
                </div>
                <pre style={S.templateBody}>{fill(t.body)}</pre>
                <Btn
                    style={{marginTop: 14}}
                    onClick={() =>
                        navigator.clipboard?.writeText(
                            `Subject: ${fill(t.subject)}\n\n${fill(t.body)}`
                        )
                    }
                >
                    Copy to Clipboard
                </Btn>
            </div>
        </div>
    );
}

// ─── Host Assignments ─────────────────────────────────────────────────────────
function HostAssignments({guests, updateGuest}) {
    const hosts = guests.filter((g) => g.type === "host");
    const clients = guests.filter((g) => g.type === "client");

    const unassigned = clients.filter(
        (g) => !g.hostId || !hosts.find((h) => h.id === g.hostId)
    );

    return (
        <div>
            <PH title="Host Assignments" action={null}/>
            <p style={S.hint}>
                Assign each client guest to a company host who will be their primary
                point of contact during the event.
            </p>

            {/* Summary cards per host */}
            {hosts.length === 0 && (
                <EmptyCard>
                    No hosts added yet. Go to Guests and add team members as "Company
                    Host" type.
                </EmptyCard>
            )}

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))",
                    gap: 16,
                    marginBottom: 24,
                }}
            >
                {hosts.map((host) => {
                    const assigned = clients.filter((g: { hostId: any; }) => g.hostId === host.id);
                    return (
                        <div
                            key={host.id}
                            style={{...S.card, borderTop: `3px solid ${GACC}`}}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    marginBottom: 14,
                                }}
                            >
                                <div
                                    style={{
                                        ...S.avatar,
                                        background: GACC,
                                        width: 36,
                                        height: 36,
                                        fontSize: 14,
                                    }}
                                >
                                    {(host.name || "?")[0].toUpperCase()}
                                </div>
                                <div>
                                    <div style={{fontWeight: 700, fontSize: 16, color: G2}}>
                                        {host.name || "Unnamed Host"}
                                    </div>
                                    <div style={{fontSize: 12, color: DIM, marginTop: 1}}>
                                        {host.company || ""}
                                    </div>
                                </div>
                                <div style={{marginLeft: "auto", textAlign: "right"}}>
                                    <div
                                        style={{
                                            fontSize: 22,
                                            fontWeight: 700,
                                            color: GACC,
                                            fontFamily: "Georgia,serif",
                                        }}
                                    >
                                        {assigned.length}
                                    </div>
                                    <div style={{fontSize: 11, color: DIM}}>guests</div>
                                </div>
                            </div>

                            {assigned.length === 0 ? (
                                <div
                                    style={{
                                        color: DIM,
                                        fontSize: 13,
                                        fontStyle: "italic",
                                        padding: "8px 0",
                                    }}
                                >
                                    No clients assigned yet.
                                </div>
                            ) : (
                                assigned.map((client: { id: Key; name: any; company: any; status: string; }) => (
                                    <div
                                        key={client.id}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            padding: "8px 0",
                                            borderTop: `1px solid ${BORDER}`,
                                        }}
                                    >
                                        <div
                                            style={{ display: "flex", alignItems: "center", gap: 8 }}
                                        >
                                            <div
                                                style={{
                                                    ...S.avatar,
                                                    background: "#2a6496",
                                                    width: 28,
                                                    height: 28,
                                                    fontSize: 12,
                                                }}
                                            >
                                                {(client.name || "?")[0].toUpperCase()}
                                            </div>
                                            <div>
                                                <div
                                                    style={{
                                                        fontSize: 14,
                                                        fontWeight: 600,
                                                        color: WHITE,
                                                    }}
                                                >
                                                    {client.name || "Unnamed"}
                                                </div>
                                                <div style={{ fontSize: 11, color: DIM }}>
                                                    {client.company || ""}
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            style={{ display: "flex", alignItems: "center", gap: 8 }}
                                        >
                      <span
                          style={{
                              ...S.chip,
                              fontSize: 11,
                              ...(client.status === "confirmed"
                                  ? {
                                      background: "rgba(74,158,74,0.25)",
                                      color: "#7ecf7e",
                                  }
                                  : client.status === "declined"
                                      ? {
                                          background: "rgba(192,57,43,0.2)",
                                          color: "#e88070",
                                      }
                                      : {
                                          background: "rgba(201,168,76,0.2)",
                                          color: G2,
                                      }),
                          }}
                      >
                        {client.status === "confirmed"
                            ? "✅"
                            : client.status === "declined"
                                ? "❌"
                                : "💌"}
                      </span>
                                            <button
                                                style={{
                                                    ...S.btnXs,
                                                    color: "#e88070",
                                                    borderColor: "rgba(192,57,43,0.3)",
                                                }}
                                                onClick={() => updateGuest(client.id, { hostId: "" })}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}

                            {/* Assign dropdown */}
                            <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                                <Sel
                                    style={{ flex: 1, fontSize: 13 }}
                                    value=""
                                    onChange={(e) => {
                                        if (e.target.value)
                                            updateGuest(e.target.value, { hostId: host.id });
                                    }}
                                >
                                    <option value="">+ Assign a client…</option>
                                    {clients
                                        .filter((g) => g.hostId !== host.id)
                                        .map((g) => (
                                            <option key={g.id} value={g.id}>
                                                {g.name || "Unnamed"}
                                                {g.hostId && hosts.find((h) => h.id === g.hostId)
                                                    ? " (reassign)"
                                                    : ""}
                                            </option>
                                        ))}
                                </Sel>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Unassigned clients */}
            {unassigned.length > 0 && (
                <>
                    <div
                        style={{
                            fontWeight: 700,
                            fontSize: 16,
                            color: G2,
                            marginBottom: 12,
                        }}
                    >
                        ⚠️ Unassigned Clients ({unassigned.length})
                    </div>
                    <div style={S.card}>
                        {unassigned.map((client) => (
                            <div
                                key={client.id}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: "10px 0",
                                    borderBottom: `1px solid ${BORDER}`,
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    <div
                                        style={{
                                            ...S.avatar,
                                            background: "#2a6496",
                                            width: 32,
                                            height: 32,
                                            fontSize: 13,
                                        }}
                                    >
                                        {(client.name || "?")[0].toUpperCase()}
                                    </div>
                                    <div>
                                        <div
                                            style={{ fontSize: 14, fontWeight: 600, color: WHITE }}
                                        >
                                            {client.name || "Unnamed"}
                                        </div>
                                        <div style={{ fontSize: 12, color: DIM }}>
                                            {client.company || ""}
                                        </div>
                                    </div>
                                </div>
                                <Sel
                                    style={{ width: 180, fontSize: 13 }}
                                    value={client.hostId || ""}
                                    onChange={(e) =>
                                        updateGuest(client.id, { hostId: e.target.value })
                                    }
                                >
                                    <option value="">— Assign host —</option>
                                    {hosts.map((h) => (
                                        <option key={h.id} value={h.id}>
                                            {h.name || "Unnamed Host"}
                                        </option>
                                    ))}
                                </Sel>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {clients.length === 0 && hosts.length > 0 && (
                <EmptyCard>
                    No client guests added yet. Go to Guests to add them.
                </EmptyCard>
            )}
        </div>
    );
}

// ─── Settings ─────────────────────────────────────────────────────────────────
function SettingsPage({ settings, setSettings }) {
    const upd = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
    const [showKey, setShowKey] = useState(false);
    return (
        <div>
            <PH title="Settings" action={null}/>
            <div style={S.card}>
                <div style={S.formGrid}>
                    <Field label="House Name">
                        <Inp
                            value={settings.houseName}
                            onChange={(e) => upd("houseName", e.target.value)}
                        />
                    </Field>
                    <Field label="Company Name">
                        <Inp
                            value={settings.companyName}
                            onChange={(e) => upd("companyName", e.target.value)}
                        />
                    </Field>
                    <Field label="Event Year">
                        <Inp
                            value={settings.eventYear}
                            onChange={(e) => upd("eventYear", e.target.value)}
                        />
                    </Field>
                </div>
                <Field label="House Address">
                    <Inp
                        value={settings.houseAddress}
                        onChange={(e) => upd("houseAddress", e.target.value)}
                    />
                </Field>
            </div>
            <div style={{ ...S.card, marginTop: 16, borderTop: `3px solid ${G}` }}>
                <div
                    style={{ fontWeight: 700, fontSize: 14, color: G2, marginBottom: 6 }}
                >
                    ✨ AI Badge Suggester
                </div>
                <p style={{ ...S.hint, marginBottom: 12 }}>
                    Enter your Anthropic API key to enable the AI allocation suggestion
                    feature in the Reservations tab. Your key is saved locally in your
                    browser and never sent anywhere except the Anthropic API.
                </p>
                <Field label="Anthropic API Key">
                    <div style={{ display: "flex", gap: 8 }}>
                        <Inp
                            type={showKey ? "text" : "password"}
                            value={settings.apiKey || ""}
                            onChange={(e) => upd("apiKey", e.target.value)}
                            placeholder="sk-ant-..."
                            style={{ flex: 1, fontFamily: "monospace", fontSize: 13 }}
                        />
                        <button style={S.btnSec} onClick={() => setShowKey((v) => !v)}>
                            {showKey ? "Hide" : "Show"}
                        </button>
                    </div>
                </Field>
                {settings.apiKey && (
                    <p
                        style={{
                            ...S.hint,
                            marginTop: 8,
                            marginBottom: 0,
                            color: "#7ecf7e",
                        }}
                    >
                        ✅ API key saved — AI suggestions are enabled.
                    </p>
                )}
                <p style={{ ...S.hint, marginTop: 8, marginBottom: 0 }}>
                    Get your API key at{" "}
                    <span style={{ color: G2 }}>console.anthropic.com</span>
                </p>
            </div>
            <p style={S.hint}>
                Changes save automatically. Manage rooms in the Rooms tab.
            </p>
        </div>
    );
}

// ─── Primitives ───────────────────────────────────────────────────────────────
function PH({ title, action }) {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 24,
                flexWrap: "wrap",
                gap: 10,
            }}
        >
            <h2
                style={{
                    margin: 0,
                    fontFamily: "Georgia,serif",
                    fontSize: 28,
                    fontWeight: 700,
                    color: G2,
                }}
            >
                {title}
            </h2>
            {action}
        </div>
    );
}
function FilterRow({ options, value, onChange }) {
    return (
        <div
            style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}
        >
            {options.map(([k, label]) => (
                <button
                    key={k}
                    style={{
                        padding: "6px 18px",
                        borderRadius: 20,
                        border: `1px solid ${value === k ? G : BORDER}`,
                        background: value === k ? "rgba(201,168,76,0.18)" : "transparent",
                        color: value === k ? G2 : DIM,
                        cursor: "pointer",
                        fontSize: 13,
                        fontFamily: "Georgia,serif",
                        transition: "all .15s",
                    }}
                    onClick={() => onChange(k)}
                >
                    {label}
                </button>
            ))}
        </div>
    );
}
function Field({ label, children, style={} }) {
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, ...style }}>
            <label
                style={{
                    fontSize: 10,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: DIM,
                    fontWeight: 700,
                }}
            >
                {label}
            </label>
            {children}
        </div>
    );
}
const Inp = ({ style={}, ...props }) => (
    <input style={{ ...S.input, ...style }} {...props} />
);
const Sel = ({ style={}, children, ...props }) => (
    <select style={{ ...S.input, ...style }} {...props}>
        {children}
    </select>
);
const Btn = ({ onClick, children, style={} }) => (
    <button style={{ ...S.btn, ...style }} onClick={onClick}>
        {children}
    </button>
);
const BtnSec = ({ onClick, children }) => (
    <button style={S.btnSec} onClick={onClick}>
        {children}
    </button>
);
function EmptyCard({ children }) {
    return (
        <div
            style={{
                ...S.card,
                textAlign: "center",
                color: DIM,
                fontStyle: "italic",
                padding: 40,
            }}
        >
            {children}
        </div>
    );
}
function EmptyState({ children }) {
    return (
        <div
            style={{
                color: DIM,
                fontSize: 13,
                fontStyle: "italic",
                padding: "8px 0",
            }}
        >
            {children}
        </div>
    );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
    sidebar: {
        width: 220,
        flexShrink: 0,
        background: "#163016",
        display: "flex",
        flexDirection: "column" as const,
        minHeight: "100vh",
        borderRight: `1px solid ${BORDER}`,
    },
    sideTop: { padding: "28px 20px 20px", borderBottom: `1px solid ${BORDER}` },
    sideTitle: {
        fontFamily: "Georgia,serif",
        fontSize: 22,
        fontWeight: 700,
        color: G2,
        lineHeight: 1.25,
        marginTop: 8,
    },
    sideSub: { fontSize: 12, color: DIM, marginTop: 6 },
    sideFooter: {
        padding: "16px 20px",
        fontSize: 11,
        color: DIM,
        borderTop: `1px solid ${BORDER}`,
        marginTop: "auto",
    },
    navBtn: {
        display: "flex",
        alignItems: "center" as const,
        gap: 10,
        width: "100%",
        padding: "10px 20px",
        background: "none",
        border: "none",
        color: DIM,
        fontSize: 14,
        fontFamily: "Georgia,serif",
        cursor: "pointer",
        textAlign: "left" as const,
        transition: "all .12s",
        borderLeft: "3px solid transparent",
    },
    navActive: {
        background: "rgba(201,168,76,0.12)",
        color: G2,
        borderLeftColor: G,
    },
    main: { flex: 1, padding: "32px 36px", overflowY: "auto" as const, minWidth: 0 },
    pageHeader: { marginBottom: 28 },
    pageTitle: {
        margin: 0,
        fontFamily: "Georgia,serif",
        fontSize: 36,
        fontWeight: 700,
        color: G2,
    },
    statGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))",
        gap: 14,
        marginBottom: 28,
    },
    statCard: {
        background: BG2,
        border: `1px solid ${BORDER}`,
        borderRadius: 12,
        padding: "18px 16px",
        cursor: "pointer",
        transition: "all .15s",
    },
    card: {
        background: BG2,
        border: `1px solid ${BORDER}`,
        borderRadius: 12,
        padding: 20,
        marginBottom: 16,
    },
    badgeCard: {
        background: BG3,
        border: `1px solid ${BORDER}`,
        borderRadius: 10,
        padding: 16,
    },
    progBg: {
        height: 6,
        background: "rgba(255,255,255,0.08)",
        borderRadius: 3,
        overflow: "hidden" as const,
        flex: 1,
    },
    progBar: {
        height: "100%",
        borderRadius: 3,
        transition: "width .4s",
        background: G,
    },
    chip: {
        display: "inline-block" as const,
        padding: "3px 10px",
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 600,
        background: "rgba(255,255,255,0.08)",
        color: WHITE,
        border: `1px solid ${BORDER}`,
    },
    guestCard: {
        background: BG2,
        border: `1px solid ${BORDER}`,
        borderRadius: 12,
        marginBottom: 10,
        overflow: "hidden",
    },
    guestHead: {
        display: "flex",
        alignItems: "center" as const,
        justifyContent: "space-between" as const,
        padding: "16px 20px",
        cursor: "pointer",
        userSelect: "none" as const,
    },
    avatar: {
        width: 38,
        height: 38,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center" as const,
        justifyContent: "center" as const,
        color: "#fff",
        fontWeight: 700,
        fontSize: 16,
        flexShrink: 0,
    },
    roomGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))",
        gap: 14,
        marginBottom: 20,
    },
    roomCard: {
        background: BG2,
        border: `1px solid ${BORDER}`,
        borderRadius: 12,
        padding: 16,
    },
    table: { width: "100%", borderCollapse: "collapse" as const },
    th: {
        textAlign: "left" as const,
        padding: "8px 12px",
        borderBottom: `1px solid ${BORDER}`,
        fontSize: 11,
        textTransform: "uppercase" as const,
        letterSpacing: "0.07em",
        color: DIM,
    },
    td: {
        padding: "10px 12px",
        borderBottom: `1px solid ${BORDER}`,
        fontSize: 14,
        verticalAlign: "middle" as const,
        color: WHITE,
    },
    formGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))",
        gap: 12,
        marginBottom: 12,
    },
    input: {
        padding: "8px 12px",
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
        fontFamily: "Georgia,serif",
        fontSize: 14,
        background: "rgba(255,255,255,0.06)",
        color: WHITE,
        outline: "none",
        width: "100%",
        boxSizing: "border-box" as const,
    },
    btn: {
        background: G,
        color: "#1a2e0f",
        border: "none",
        padding: "9px 20px",
        borderRadius: 8,
        cursor: "pointer",
        fontFamily: "Georgia,serif",
        fontSize: 14,
        fontWeight: 700,
        transition: "all .15s",
    },
    btnSec: {
        background: "transparent",
        color: DIM,
        border: `1px solid ${BORDER}`,
        padding: "9px 20px",
        borderRadius: 8,
        cursor: "pointer",
        fontFamily: "Georgia,serif",
        fontSize: 14,
    },
    btnDanger: {
        background: "rgba(192,57,43,0.8)",
        color: "#fff",
        border: "none",
        padding: "7px 16px",
        borderRadius: 8,
        cursor: "pointer",
        fontFamily: "Georgia,serif",
        fontSize: 13,
    },
    btnXs: {
        background: "none",
        border: `1px solid ${BORDER}`,
        color: DIM,
        padding: "2px 8px",
        borderRadius: 4,
        cursor: "pointer",
        fontSize: 12,
    },
    hint: { fontSize: 13, color: DIM, fontStyle: "italic", marginBottom: 16 },
    templateBody: {
        fontFamily: "Georgia,serif",
        fontSize: 13,
        lineHeight: 1.8,
        whiteSpace: "pre-wrap",
        background: "rgba(0,0,0,0.2)",
        padding: 16,
        borderRadius: 8,
        border: `1px solid ${BORDER}`,
        margin: 0,
        color: WHITE,
    },
};

const CSS = `
  @keyframes spin { to { transform: rotate(360deg); } }
  .nav-btn:hover  { background: rgba(201,168,76,0.08) !important; color: ${WHITE} !important; }
  .clickable:hover { background: rgba(255,255,255,0.03) !important; }
  .stat-card:hover { background: ${BG3} !important; border-color: ${G} !important; }
  .template-pill:hover { background: rgba(201,168,76,0.3) !important; color: ${G2} !important; }
  input:focus, select:focus, textarea:focus { border-color: ${G} !important; box-shadow: 0 0 0 2px rgba(201,168,76,0.15) !important; outline: none !important; }
  input[type=checkbox] { accent-color: ${G}; width: 15px; height: 15px; cursor: pointer; }
  option { background: #1f421f; color: ${WHITE}; }
  * { box-sizing: border-box; }
  body { margin: 0; background: ${BG}; }
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: ${BG}; }
  ::-webkit-scrollbar-thumb { background: rgba(201,168,76,0.3); border-radius: 3px; }
`;
